import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as fabric from 'fabric';
import {
    Layout, Users, Play, RefreshCw, Undo2, Move, Trash2, Pencil, Spline, ArrowUpRight, Zap, Check, X, Plus, Circle, Minus, MoveHorizontal, Shield, Square, Link, Unlink

} from 'lucide-react';
import { Button } from './ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "./ui/tooltip";
import { cn } from '../lib/utils';
import { useTranslation } from 'react-i18next';

// --- FABRIC SAFETY PATCH ---
// Fix for canvas disposal errors during async operations
// After dispose(), Fabric's internal references become invalid
// This patch ensures clearContext checks for valid context before executing

const originalClearContext = fabric.StaticCanvas.prototype.clearContext;
fabric.StaticCanvas.prototype.clearContext = function (ctx) {
    if (ctx && typeof ctx.clearRect === 'function') {
        originalClearContext.call(this, ctx);
    }
};

// --- FABRIC EXTENSIONS ---
// Override Polyline render to support curved paths if data.isCurved is true
const originalPolylineRender = fabric.Polyline.prototype._render;
fabric.Polyline.prototype._render = function (ctx) {
    const curvedPoints = this.data?.curvedPoints || {};
    const hasAnyCurve = Object.values(curvedPoints).some(v => v);
    const globalCurved = this.data?.isCurved;

    if ((globalCurved || hasAnyCurve) && this.points.length > 2) {
        const p = this.points;
        const offset = this.pathOffset;
        ctx.beginPath();
        ctx.moveTo(p[0].x - offset.x, p[0].y - offset.y);

        for (let i = 1; i < p.length - 1; i++) {
            const isCurved = globalCurved || curvedPoints[i];
            const xc = (p[i].x + p[i + 1].x) / 2;
            const yc = (p[i].y + p[i + 1].y) / 2;

            if (isCurved) {
                ctx.quadraticCurveTo(
                    p[i].x - offset.x,
                    p[i].y - offset.y,
                    xc - offset.x,
                    yc - offset.y
                );
            } else {
                ctx.lineTo(p[i].x - offset.x, p[i].y - offset.y);
                ctx.lineTo(xc - offset.x, yc - offset.y);
            }
        }
        ctx.lineTo(
            p[p.length - 1].x - offset.x,
            p[p.length - 1].y - offset.y
        );
        this._renderStroke(ctx);
    } else {
        originalPolylineRender.call(this, ctx);
    }
};

// --- CONSTANTS ---
const FIELD_WIDTH = 700;
const FIELD_HEIGHT_FULL = 700;
const FIELD_HEIGHT_FORMATION = 240; // Focused view height
const YARD_PX = 12; // 1 yard = 12px approx based on 700px height covering ~58 yards

// Standard Formation positions relative to Center (x=400, y=LOS)
// We will dynamically adjust Y based on LOS
const FORMATION_OFFSETS = {
    spread: [
        { role: 'QB', x: 0, y: 5 }, // 5yds back
        { role: 'C', x: 0, y: 0 },
        { role: 'LT', x: -60, y: 0 }, // -5 yards approx
        { role: 'RT', x: 60, y: 0 },
        { role: 'LG', x: -30, y: 0 },
        { role: 'RG', x: 30, y: 0 },
        { role: 'WR1', x: -300, y: 0 }, // Wide left
        { role: 'WR2', x: 300, y: 0 }, // Wide right
        { role: 'WR3', x: -200, y: 1 }, // Slot left
        { role: 'WR4', x: 200, y: 1 }, // Slot right
        { role: 'RB', x: 40, y: 5 }, // Side of QB
    ],
    shotgun: [
        { role: 'QB', x: 0, y: 5 },
        { role: 'C', x: 0, y: 0 },
        { role: 'LG', x: -30, y: 0 },
        { role: 'RG', x: 30, y: 0 },
        { role: 'LT', x: -60, y: 0 },
        { role: 'RT', x: 60, y: 0 },
        { role: 'RB', x: 40, y: 5 },
        { role: 'WR1', x: -300, y: 0 },
        { role: 'WR2', x: 300, y: 0 },
        { role: 'WR3', x: -200, y: 1 },
        { role: 'TE', x: 90, y: 0 },
    ],
    iFormation: [
        { role: 'QB', x: 0, y: 1 },
        { role: 'C', x: 0, y: 0 },
        { role: 'LG', x: -30, y: 0 },
        { role: 'RG', x: 30, y: 0 },
        { role: 'LT', x: -60, y: 0 },
        { role: 'RT', x: 60, y: 0 },
        { role: 'FB', x: 0, y: 4 },
        { role: 'HB', x: 0, y: 7 },
        { role: 'TE', x: 90, y: 0 },
        { role: 'WR1', x: -300, y: 0 },
        { role: 'WR2', x: 300, y: 0 },
    ],
    pistol: [
        { role: 'QB', x: 0, y: 4 },
        { role: 'C', x: 0, y: 0 },
        { role: 'LG', x: -30, y: 0 },
        { role: 'RG', x: 30, y: 0 },
        { role: 'LT', x: -60, y: 0 },
        { role: 'RT', x: 60, y: 0 },
        { role: 'HB', x: 0, y: 6 }, // Behind QB
        { role: 'TE', x: 90, y: 0 },
        { role: 'WR1', x: -300, y: 0 },
        { role: 'WR2', x: 300, y: 0 },
        { role: 'WR3', x: -200, y: 1 },
    ],
    empty: [
        { role: 'QB', x: 0, y: 5 },
        { role: 'C', x: 0, y: 0 },
        { role: 'LG', x: -30, y: 0 },
        { role: 'RG', x: 30, y: 0 },
        { role: 'LT', x: -60, y: 0 },
        { role: 'RT', x: 60, y: 0 },
        { role: 'WR1', x: -300, y: 0 },
        { role: 'WR2', x: 300, y: 0 },
        { role: 'WR3', x: -200, y: 1 },
        { role: 'WR4', x: 200, y: 1 },
        { role: 'WR5', x: -100, y: 1 },
    ],
    tripsRight: [
        { role: 'QB', x: 0, y: 5 },
        { role: 'C', x: 0, y: 0 },
        { role: 'LG', x: -30, y: 0 },
        { role: 'RG', x: 30, y: 0 },
        { role: 'LT', x: -60, y: 0 },
        { role: 'RT', x: 60, y: 0 },
        { role: 'RB', x: -40, y: 5 },
        { role: 'WR1', x: -300, y: 0 },
        { role: 'WR2', x: 150, y: 0 },
        { role: 'WR3', x: 225, y: 1 },
        { role: 'WR4', x: 300, y: 0 },
    ]
};

// Increased Y spacing to ensure players don't overlap LOS visually (Radius ~14px)
const DEFENSE_FORMATIONS = {
    '4-3': [
        { role: 'DT', x: -25, y: -40 },
        { role: 'DT', x: 25, y: -40 },
        { role: 'DE', x: -90, y: -40 },
        { role: 'DE', x: 90, y: -40 },
        { role: 'MLB', x: 0, y: -110 },
        { role: 'OLB', x: -110, y: -100 },
        { role: 'OLB', x: 110, y: -100 },
        { role: 'CB', x: -300, y: -60 },
        { role: 'CB', x: 300, y: -60 },
        { role: 'FS', x: -60, y: -220 },
        { role: 'SS', x: 60, y: -200 },
    ],
    '3-4': [
        { role: 'NT', x: 0, y: -40 },
        { role: 'DE', x: -70, y: -40 },
        { role: 'DE', x: 70, y: -40 },
        { role: 'ILB', x: -40, y: -110 },
        { role: 'ILB', x: 40, y: -110 },
        { role: 'OLB', x: -120, y: -90 },
        { role: 'OLB', x: 120, y: -90 },
        { role: 'CB', x: -300, y: -60 },
        { role: 'CB', x: 300, y: -60 },
        { role: 'FS', x: -60, y: -220 },
        { role: 'SS', x: 60, y: -200 },
    ],
    'nickel': [
        { role: 'DT', x: -30, y: -40 },
        { role: 'DT', x: 30, y: -40 },
        { role: 'DE', x: -100, y: -40 },
        { role: 'DE', x: 100, y: -40 },
        { role: 'LB', x: -60, y: -110 },
        { role: 'LB', x: 60, y: -110 },
        { role: 'CB', x: -300, y: -60 },
        { role: 'CB', x: 300, y: -60 },
        { role: 'NB', x: 100, y: -90 },
        { role: 'FS', x: -70, y: -220 },
        { role: 'SS', x: 70, y: -200 },
    ],
    'dime': [
        { role: 'DT', x: -35, y: -40 },
        { role: 'DT', x: 35, y: -40 },
        { role: 'DE', x: -110, y: -40 },
        { role: 'DE', x: 110, y: -40 },
        { role: 'MLB', x: 0, y: -110 },
        { role: 'CB', x: -300, y: -60 },
        { role: 'CB', x: 300, y: -60 },
        { role: 'DB', x: -100, y: -90 },
        { role: 'DB', x: 100, y: -90 },
        { role: 'FS', x: -80, y: -240 },
        { role: 'SS', x: 80, y: -220 },
    ],
    '5-2': [
        { role: 'NG', x: 0, y: -40 },
        { role: 'DT', x: -50, y: -40 },
        { role: 'DT', x: 50, y: -40 },
        { role: 'DE', x: -100, y: -40 },
        { role: 'DE', x: 100, y: -40 },
        { role: 'LB', x: -60, y: -110 },
        { role: 'LB', x: 60, y: -110 },
        { role: 'CB', x: -300, y: -60 },
        { role: 'CB', x: 300, y: -60 },
        { role: 'FS', x: -50, y: -220 },
        { role: 'SS', x: 50, y: -200 },
    ],
    '4-4': [
        { role: 'DT', x: -25, y: -40 },
        { role: 'DT', x: 25, y: -40 },
        { role: 'DE', x: -90, y: -40 },
        { role: 'DE', x: 90, y: -40 },
        { role: 'ILB', x: -30, y: -110 },
        { role: 'ILB', x: 30, y: -110 },
        { role: 'OLB', x: -120, y: -100 },
        { role: 'OLB', x: 120, y: -100 },
        { role: 'CB', x: -300, y: -60 },
        { role: 'CB', x: 300, y: -60 },
        { role: 'FS', x: 0, y: -220 },
    ],
    'goal line': [
        { role: 'NG', x: 0, y: -40 },
        { role: 'DT', x: -30, y: -40 },
        { role: 'DT', x: 30, y: -40 },
        { role: 'DE', x: -70, y: -40 },
        { role: 'DE', x: 70, y: -40 },
        { role: 'LB', x: -100, y: -50 }, // Back off line slightly
        { role: 'LB', x: 100, y: -50 },
        { role: 'MLB', x: 0, y: -90 },
        { role: 'CB', x: -250, y: -60 },
        { role: 'CB', x: 250, y: -60 },
        { role: 'SS', x: 0, y: -160 },
    ],
};

// --- MATH HELPERS ---
const getZoneIntersection = (p1, zone) => {
    // p1: Point {x, y} (Start of route)
    // zone: Fabric Object (Rect or Ellipse)

    // 1. Transform p1 to Zone's Local Coordinate System
    const invertMatrix = fabric.util.invertTransform(zone.calcTransformMatrix());
    const locals = fabric.util.transformPoint(p1, invertMatrix);
    const localP = { x: locals.x, y: locals.y };

    // Zone Center in Local is (0,0) (because originX/Y are center)
    // Ray is from LocalP to (0,0).
    // Valid intersection is the one between LocalP and (0,0).

    let intersect = null;

    if (zone.shape === 'rect' || zone.type === 'rect') {
        // Rectangle Intersection (AABB in local space)
        // Bounds: [-w/2, w/2], [-h/2, h/2]
        const w2 = zone.width / 2;
        const h2 = zone.height / 2;

        // Ray: P = LocalP + t * (0 - LocalP) -> P = LocalP * (1 - t)
        // We want t where P is on boundary.
        // Simplified: Find intersection of segment (LocalP -> 0,0) with box edges.

        let minT = 0;
        let maxT = 1;

        // Cohen-Sutherland-like parameter clipping? 
        // Or just direct ray slab intersection.
        // Ray d = -LocalP.
        // P(t) = LocalP + t * d.

        // Check X planes
        // x = +/- w2
        // px + t * dx = +/- w2  =>  t = (+/- w2 - px) / dx

        const dx = -localP.x;
        const dy = -localP.y;

        const tValues = [];

        if (Math.abs(dx) > 0.0001) {
            tValues.push((-w2 - localP.x) / dx);
            tValues.push((w2 - localP.x) / dx);
        }
        if (Math.abs(dy) > 0.0001) {
            tValues.push((-h2 - localP.y) / dy);
            tValues.push((h2 - localP.y) / dy);
        }

        // Filter t in [0, 1] and pick smallest valid t that is effectively entering the box (or just closest to P)
        // Since P is OUTSIDE (usually) and Center is INSIDE, there is exactly one intersection with t in [0, 1].
        // We want the t closest to 0 (closest to P)? No, closest to P means t=0. Closest to P that is ON Boundary.
        // P is start (outside), Target is Center (inside).
        // Intersection is boundaries.
        // We want the first intersection along the ray.

        const validT = tValues.filter(t => t >= 0 && t <= 1).sort((a, b) => a - b)[0];

        if (validT !== undefined) {
            intersect = {
                x: localP.x + validT * dx,
                y: localP.y + validT * dy
            };
        }

    } else if (zone.shape === 'ellipse' || zone.type === 'ellipse') {
        // Ellipse Intersection
        // x^2/a^2 + y^2/b^2 = 1
        // P(t) = LocalP + t * d  (where d = -LocalP) 
        // P(t) = LocalP * (1 - t)
        // Substitute: (px*(1-t))^2 / a^2 + (py*(1-t))^2 / b^2 = 1
        // (1-t)^2 * [ px^2/a^2 + py^2/b^2 ] = 1
        // let K = px^2/rx^2 + py^2/ry^2
        // (1-t)^2 * K = 1
        // (1-t)^2 = 1/K
        // 1-t = +/- sqrt(1/K)
        // t = 1 - sqrt(1/K)  (since we want t close to 0 if K > 1 (outside))
        // If K < 1, point is inside, no intersection on segment? (Shouldn't happen for Defense drawing logic usually)

        const rx = zone.rx;
        const ry = zone.ry;
        const K = (localP.x * localP.x) / (rx * rx) + (localP.y * localP.y) / (ry * ry);

        if (K > 0) {
            const factor = Math.sqrt(1 / K); // This is 1 / normalized_dist
            // Intersection point on ellipse boundary is simply LocalP scaled by factor?
            // Let's verify: P_edge = P * factor.
            // (px*f)^2/a^2 = f^2 * (px^2/a^2)
            // Sum = f^2 * K = (1/K) * K = 1. Yes.

            intersect = {
                x: localP.x * factor,
                y: localP.y * factor
            };
        }
    }

    // 2. Transform Intersect back to Global
    if (intersect) {
        return fabric.util.transformPoint(intersect, zone.calcTransformMatrix());
    }
    return null;
};

const PlayDesigner = ({ initialData, onSave, onCancel, playName, playbookType = 'offense' }) => {
    const { t } = useTranslation();
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const fabricCanvas = useRef(null);

    // State
    const [selectedObject, setSelectedObject] = useState(null);
    const selectedObjectRef = useRef(selectedObject);
    useEffect(() => { selectedObjectRef.current = selectedObject; }, [selectedObject]);

    const [drawingMode, setDrawingMode] = useState(null); // 'pass', 'motion', 'block', 'zone', 'route'
    const [showZoneMenu, setShowZoneMenu] = useState(false);
    const activeShapeType = useRef(null); // 'rect' or 'ellipse'
    const activeShapeStart = useRef(null); // {x, y}
    const activeDrawingObject = useRef(null); // Shape being drawn
    const lastZoneDrawTime = useRef(0); // Debounce for zone drawing
    const drawingModeRef = useRef(drawingMode);
    const connectionSourceRef = useRef(null); // Reliable ref for automated connect source
    useEffect(() => { drawingModeRef.current = drawingMode; }, [drawingMode]);

    const [showFormationPicker, setShowFormationPicker] = useState(!initialData);
    const [designerMode, setDesignerMode] = useState('play'); // 'play' or 'formation'
    const [routeEndType, setRouteEndType] = useState('arrow'); // 'arrow' or 'block'
    const [isEditMode, setIsEditMode] = useState(false); // Toggle between Draw (Extend) and Edit (Move)
    const [isCurveMode, setIsCurveMode] = useState(false); // Toggle Curve Mode
    const isEditModeRef = useRef(isEditMode);
    const isCurveModeRef = useRef(isCurveMode);

    useEffect(() => { isCurveModeRef.current = isCurveMode; }, [isCurveMode]);
    // Moved useEffect down to avoid ReferenceError

    const [isMoveMode, setIsMoveMode] = useState(false); // Toggle player movement
    const isMoveModeRef = useRef(isMoveMode);

    // Forward refs for stable event listeners (Avoid stale closures with Fabric)
    const onObjectMovingRef = useRef(null);

    useEffect(() => {
        isMoveModeRef.current = isMoveMode;

        // Update selection properties immediately when mode changes
        if (fabricCanvas.current) {
            const canvas = fabricCanvas.current;
            canvas.getObjects().forEach(obj => {
                // Widen check: If it's a Group, assume it's a player (unless it has specific non-player data)
                const isPlayer = (obj.data?.type === 'O' || obj.data?.type === 'D') || (obj.type === 'group');

                if (isPlayer) {
                    const shouldLock = !isMoveMode;
                    obj.set({
                        lockMovementX: shouldLock,
                        lockMovementY: shouldLock,
                        lockRotation: shouldLock,
                        lockScalingX: shouldLock,
                        lockScalingY: shouldLock,
                        hoverCursor: shouldLock ? 'default' : 'move',
                        selectable: !shouldLock, // STRICT: Unselectable if locked
                        evented: true,
                        hasControls: !shouldLock, // Restore controls when unlocked
                        hasBorders: !shouldLock
                    });
                }
            });
            canvas.requestRenderAll();
        }
    }, [isMoveMode]);
    const [customFormations, setCustomFormations] = useState(() => {
        const saved = localStorage.getItem('snapp_custom_formations');
        return saved ? JSON.parse(saved) : {};
    });
    const [isPlaying, setIsPlaying] = useState(false);
    const isPlayingRef = useRef(false);
    const originalPositions = useRef({});

    // Centralized Render Loop for Animations
    useEffect(() => {
        let rafId;
        const render = () => {
            if (isPlayingRef.current) {
                fabricCanvas.current?.renderAll();
                rafId = requestAnimationFrame(render);
            }
        };
        if (isPlaying) {
            rafId = requestAnimationFrame(render);
        }
        return () => cancelAnimationFrame(rafId);
    }, [isPlaying]);

    // Toggle Player Movement Lock
    useEffect(() => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;

        canvas.getObjects().forEach(obj => {
            if (obj.data?.type === 'O' || obj.data?.type === 'D') {
                obj.set({
                    lockMovementX: !isMoveMode,
                    lockMovementY: !isMoveMode,
                    hoverCursor: isMoveMode ? 'move' : 'default',
                    perPixelTargetFind: false // easier selection?
                });
            }
        });
        canvas.requestRenderAll();
    }, [isMoveMode]);
    const [losY, setLosY] = useState(630); // Default LOS Y position (40 yard line in new SVG)
    const losYRef = useRef(losY);

    // Sync Ref with State
    useEffect(() => {
        losYRef.current = losY;
    }, [losY]);

    // History for Undo
    const [history, setHistory] = useState([]);

    // Tools references
    const tempLine = useRef(null);
    const tempArrow = useRef(null);
    const losLineObj = useRef(null);
    const losHandleObj = useRef(null);
    const isExtendingRef = useRef(false);

    // Route Drawing Refs (Polyline System)
    const activePolyline = useRef(null); // The Polyline object currently being drawn/edited
    const previewLine = useRef(null);
    const polylineHandles = useRef([]); // Editors for the polyline vertices
    const activeRouteHandlers = useRef({}); // Store active event listeners for cleanup



    // --- UTILITIES ---
    const saveToHistory = useCallback(() => {
        if (!fabricCanvas.current) return;
        // Include 'pathOffset' for Polyline serialization to prevent coordinate drift on undo
        setHistory(prev => [...prev.slice(-19), fabricCanvas.current.toJSON(['data', 'pathOffset'])]);
    }, []);

    // --- POLYLINE HELPER FUNCTIONS ---

    const cleanupPolylineHandles = useCallback(() => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;
        if (polylineHandles.current.length > 0) {
            canvas.remove(...polylineHandles.current);
            polylineHandles.current = [];
        }
    }, []);

    const cleanupPreviewLine = useCallback(() => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;
        if (previewLine.current) {
            canvas.remove(previewLine.current);
            previewLine.current = null;
        }
    }, []);


    const removeDuplicateRoutes = useCallback((playerId) => {
        const canvas = fabricCanvas.current;
        if (!canvas || !playerId) return;
        const pid = String(playerId).trim();
        const routes = canvas.getObjects().filter(o => {
            if (o.data?.type !== 'route_polyline') return false;
            const oid = String(o.data?.id || '').trim();
            const opid = String(o.data?.parentId || '').trim();
            return oid === pid || opid === pid;
        });
        const subs = ['route', 'motion'];
        subs.forEach(s => {
            const matches = routes.filter(r => (r.data?.subType || 'route') === s);
            if (matches.length > 1) {
                // Keep the one connected to a zone, or just the first one
                matches.sort((a, b) => {
                    const aConn = a.data?.connectedZoneId ? 1 : 0;
                    const bConn = b.data?.connectedZoneId ? 1 : 0;
                    return bConn - aConn;
                });
                for (let i = 1; i < matches.length; i++) {
                    const toRemove = matches[i];
                    const rid = toRemove.data?.id;
                    if (rid) {
                        canvas.getObjects().filter(o =>
                            (o.data?.type === 'route_end' && o.data?.routeId === rid) ||
                            (o.data?.type === 'poly_handle' && o.data?.routeId === rid) ||
                            (o.data?.type === 'curve_handle' && o.data?.routeId === rid)
                        ).forEach(obj => canvas.remove(obj));
                    }
                    canvas.remove(toRemove);
                }
            }
        });
    }, []);

    // Update Arrowhead Position/Rotation
    const updateRouteArrow = useCallback((poly, forcedType) => {
        const canvas = fabricCanvas.current;
        if (!canvas || !poly || poly.points.length < 2) return;

        // Determine Type
        if (forcedType) {
            poly.data.endType = forcedType;
        }
        const endType = poly.data.endType || 'arrow'; // Default to arrow

        // Find existing end object
        const routeId = poly.data?.id;
        let endObj = canvas.getObjects().find(o => o.data?.type === 'route_end' && o.data?.routeId === routeId);

        // Handle 'none' type (remove existing if any)
        if (endType === 'none') {
            if (endObj) canvas.remove(endObj);
            return;
        }

        // Check if existing object matches type and subType
        if (endObj) {
            const currentSubType = endObj.data?.subType;
            if (currentSubType !== endType) {
                canvas.remove(endObj);
                endObj = null;
            }
        }

        // Get last segment points (absolute)
        let p2 = poly.points[poly.points.length - 1]; // Tip
        let p1 = poly.points[poly.points.length - 2]; // Previous

        // Handle case where P1 == P2 (duplicate points)
        if (p1.x === p2.x && p1.y === p2.y && poly.points.length > 2) {
            p1 = poly.points[poly.points.length - 3];
        }

        // Convert to absolute
        const matrix = poly.calcTransformMatrix();
        const absP1 = fabric.util.transformPoint({ x: p1.x - (poly.pathOffset.x || 0), y: p1.y - (poly.pathOffset.y || 0) }, matrix);
        const absP2 = fabric.util.transformPoint({ x: p2.x - (poly.pathOffset.x || 0), y: p2.y - (poly.pathOffset.y || 0) }, matrix);

        // Calculate Angle & Offset
        const dx = absP2.x - absP1.x;
        const dy = absP2.y - absP1.y;
        let angle = Math.atan2(dy, dx) * (180 / Math.PI);
        const len = Math.sqrt(dx * dx + dy * dy) || 1;

        // Offset Logic
        // Arrow: 0 offset to ensure connection (OriginY 'bottom' handles alignment)
        const offsetDist = 0;
        const offsetX = 0;
        const offsetY = 0;

        angle += 90;

        if (!endObj) {
            if (endType === 'stop') { // Refined Stop: smaller, no border
                endObj = new fabric.Circle({
                    radius: 4,
                    fill: '#facc15', // Yellow
                    strokeWidth: 0,
                    selectable: false,
                    evented: false,
                    originX: 'center',
                    originY: 'center',
                    data: { type: 'route_end', subType: 'stop', routeId: routeId }
                });
            } else if (endType === 'block') { // Block: Perpendicular Bar
                endObj = new fabric.Rect({
                    width: 16,
                    height: 4,
                    fill: '#facc15', // Yellow
                    selectable: false,
                    evented: false,
                    originX: 'center',
                    originY: 'center',
                    data: { type: 'route_end', subType: 'block', routeId: routeId }
                });
            } else {
                // Arrow
                endObj = new fabric.Triangle({
                    width: 15,
                    height: 15,
                    fill: '#facc15', // Yellow
                    selectable: false,
                    evented: false,
                    originX: 'center',
                    originY: 'bottom', // Base at point (Moves arrow forward)
                    data: { type: 'route_end', subType: 'arrow', routeId: routeId }
                });
            }
            canvas.add(endObj);
        }

        endObj.set({
            left: absP2.x + offsetX,
            top: absP2.y + offsetY,
            angle: (endType === 'arrow' || endType === 'block') ? angle : 0,
            originY: (endType === 'arrow') ? 'bottom' : 'center' // Ensure Arrow is offset forward
        });

        // Ensure decoration is above routes but below players/handles
        // We bring it to front, but then we'll bring players to front in runPlay/init
        canvas.bringObjectToFront(endObj);

        // Also ensure handles are on top if editing
        if (isEditModeRef.current) {
            canvas.getObjects().filter(o => o.data?.type === 'poly_handle' || o.data?.type === 'curve_handle').forEach(h => canvas.bringObjectToFront(h));
        }

    }, [saveToHistory]);

    // Refresh handles for editing a Polyline (Moved here to resolve dependencies)
    const refreshPolylineHandles = useCallback(function internalRefresh(poly, clearExisting = true) {
        const canvas = fabricCanvas.current;
        if (!canvas || !poly) return;

        if (clearExisting) cleanupPolylineHandles();

        // STRICT EDIT MODE: Only show handles if Edit Mode OR Curve Mode is ON
        if (!isEditModeRef.current && !isCurveModeRef.current) return;

        // Calculate absolute positions of points
        const matrix = poly.calcTransformMatrix();

        const newHandles = poly.points.map((p, index) => {
            const relPoint = {
                x: p.x - (poly.pathOffset.x || 0),
                y: p.y - (poly.pathOffset.y || 0)
            };
            const pointOnCanvas = fabric.util.transformPoint(relPoint, matrix);

            const isStartPoint = (index === 0);
            const isEndPoint = (index === poly.points.length - 1);
            const isInternal = !isStartPoint && !isEndPoint;

            // Curve Mode Logic: "Only show joint (internal) handles, in Blue"
            if (isCurveModeRef.current && !isInternal) return null;

            const isThisCurved = poly.data?.curvedPoints?.[index] || poly.data?.isCurved;

            const handle = new fabric.Circle({
                radius: 6,
                fill: (isCurveModeRef.current && isInternal)
                    ? (isThisCurved ? '#3b82f6' : '#94a3b8')
                    : (isStartPoint ? '#15803d' : '#22c55e'),
                stroke: 'white',
                strokeWidth: 2,
                left: pointOnCanvas.x,
                top: pointOnCanvas.y,
                originX: 'center',
                originY: 'center',
                hasControls: false,
                hasBorders: false,
                selectable: true,
                evented: true,
                data: {
                    type: (isCurveModeRef.current && isInternal) ? 'curve_handle' : 'poly_handle',
                    routeId: poly.data?.id,
                    routeSubType: poly.data?.subType || 'route',
                    index: index
                },
                hoverCursor: (isCurveModeRef.current && isInternal) ? 'pointer' : (isStartPoint ? 'default' : 'move'),
                moveCursor: (isCurveModeRef.current && isInternal) ? 'pointer' : (isStartPoint ? 'default' : 'move')
            });

            if (isCurveModeRef.current && isInternal) {
                handle.on('mousedown', () => {
                    if (!poly.data.curvedPoints) poly.data.curvedPoints = {};
                    // Toggle
                    poly.data.curvedPoints[index] = !isThisCurved;
                    // Migrate from global to specific
                    poly.data.isCurved = false;

                    internalRefresh(poly, false); // Refresh only this route's handles? No, better refresh all for selection
                    updateRouteArrow(poly);
                    canvas?.requestRenderAll();
                    saveToHistory();
                });
            }

            return handle;
        }).filter(Boolean);

        canvas.add(...newHandles);
        newHandles.forEach(h => canvas.bringObjectToFront(h));
        polylineHandles.current = [...polylineHandles.current, ...newHandles];
        canvas.requestRenderAll();
    }, [cleanupPolylineHandles, updateRouteArrow, saveToHistory]);

    // Stable Anchor Update for Polyline Reshaping
    const updatePolylineShape = useCallback((poly, index, newAbsPos) => {
        if (!poly || !poly.points || poly.points.length < 2) return;

        // Safety: Ensure pathOffset exists (for new polylines)
        if (!poly.pathOffset) {
            poly.pathOffset = { x: 0, y: 0 };
            poly._calcDimensions();
        }

        // 1. Identify Anchor (any other point)
        // Use Index 0 as anchor usually, unless we are moving Index 0, then use Index 1.
        const anchorIndex = (index === 0) ? 1 : 0;
        const anchorPt = poly.points[anchorIndex];

        // 2. Helper to get Absolute Position of a point index
        // poly.points are stored with pathOffset added.
        // T-L of bounding box is (0,0) of the object space (if origin is left/top).
        // Actual local coordinate = point.x - pathOffset.x
        const getAbsPosition = (point, object) => {
            const matrix = object.calcTransformMatrix();
            const offsetPoint = {
                x: point.x - object.pathOffset.x,
                y: point.y - object.pathOffset.y
            };
            return fabric.util.transformPoint(offsetPoint, matrix);
        };

        const anchorStartAbs = getAbsPosition(anchorPt, poly);

        // 3. Calculate New Local Point for the Moved Handle
        // Inverse transform the absolute mouse position
        const matrix = poly.calcTransformMatrix();
        const inverted = fabric.util.invertTransform(matrix);
        const newLocal = fabric.util.transformPoint(newAbsPos, inverted);

        // Add pathOffset back to get the 'points' array value
        const newPointRaw = {
            x: newLocal.x + poly.pathOffset.x,
            y: newLocal.y + poly.pathOffset.y
        };

        poly.points[index] = new fabric.Point(newPointRaw.x, newPointRaw.y);

        // 4. Recalculate Dimensions (This shifts the internal coordinate system!)
        poly._calcDimensions();
        poly.set({ dirty: true, objectCaching: false });

        // 5. Calculate Drift and Compensate
        const anchorEndAbs = getAbsPosition(anchorPt, poly);

        const driftX = anchorEndAbs.x - anchorStartAbs.x;
        const driftY = anchorEndAbs.y - anchorStartAbs.y;

        poly.set({
            left: poly.left - driftX,
            top: poly.top - driftY
        });
        poly.setCoords();
        updateRouteArrow(poly);
        const canvas = fabricCanvas.current;
        if (canvas) canvas.requestRenderAll();
    }, [updateRouteArrow]);

    // Listener for moving objects (Zones) to update connected routes
    // Listener for moving objects (Zones) to update connected routes
    const onObjectMoving = useCallback((e) => {
        const target = e.target;
        const canvas = fabricCanvas.current;
        if (!target || !canvas) return;

        // 1. Polyline Handle Logic
        if (target.data?.type === 'poly_handle' || target.data?.type === 'curve_handle') {
            const { routeId, routeSubType, index } = target.data;
            const poly = canvas.getObjects().find(o =>
                o.data?.id === routeId &&
                o.data?.type === 'route_polyline' &&
                (o.data?.subType || 'route') === (routeSubType || 'route')
            );

            if (poly) {
                // Detach from zone if we move the tip manually
                if (index === poly.points.length - 1 && poly.data?.connectedZoneId) {
                    poly.data.connectedZoneId = null;
                }

                updatePolylineShape(poly, index, { x: target.left, y: target.top });

                // SYNC: If motion tip moves, regular route start follows
                if (poly.data?.subType === 'motion' && index === poly.points.length - 1) {
                    const regRoute = canvas.getObjects().find(o =>
                        o.data?.id === routeId &&
                        o.data?.type === 'route_polyline' &&
                        o.data?.subType === 'route'
                    );
                    if (regRoute) updatePolylineShape(regRoute, 0, { x: target.left, y: target.top });
                }
            }
            canvas.requestRenderAll();
            return;
        }

        // 2. Player & Zone Constraint Logic
        if (target.data?.type === 'O' || target.data?.type === 'D' || target.data?.type === 'zone_coverage') {
            // HIDE BORDERS while moving for cleaner look
            target.set({ hasBorders: false, hasControls: false });

            const currentLosY = losHandleObj.current ? losHandleObj.current.top : null;
            if (currentLosY !== null) {
                if (target.data.type === 'O' && target.top < currentLosY) target.top = currentLosY;
                else if (target.data.type === 'D' && target.top > currentLosY) target.top = currentLosY;
            }

            target.setCoords();
            const targetId = target.data?.id;
            const centerPos = target.getCenterPoint();

            if (targetId || target.id) {
                const targetIdStr = String(targetId || target.id);

                const tidTrim = targetIdStr.trim();
                // --- Sync Routes Owned BY this Player/Zone ---
                const ownedRoutes = canvas.getObjects().filter(o => {
                    if (o.data?.type !== 'route_polyline') return false;
                    const oid = String(o.data?.id || '').trim();
                    const opid = String(o.data?.parentId || '').trim();
                    return oid === tidTrim || opid === tidTrim;
                });

                ownedRoutes.forEach(poly => {
                    const subType = poly.data?.subType || 'route';
                    if (subType === 'motion') {
                        updatePolylineShape(poly, 0, centerPos);
                    } else if (subType === 'route') {
                        const hasMotion = ownedRoutes.some(r => (r.data?.subType || 'route') === 'motion');
                        if (!hasMotion) updatePolylineShape(poly, 0, centerPos);
                    }
                });

                // --- Sync Routes Connected TO this Zone ---
                if (target.data?.type === 'zone_coverage') {
                    const allObjects = canvas.getObjects();
                    let foundCount = 0;
                    for (let i = 0; i < allObjects.length; i++) {
                        const obj = allObjects[i];
                        const connId = obj.data?.connectedZoneId;
                        if (obj.data?.type === 'route_polyline' && connId && String(connId).trim() === targetIdStr.trim()) {
                            const lastIndex = obj.points.length - 1;
                            updatePolylineShape(obj, lastIndex, centerPos);
                            obj.setCoords();
                            obj.set({ dirty: true });
                            foundCount++;
                        }
                    }
                    if (foundCount > 0) {
                        // console.log(`[onObjectMoving] Synced ${foundCount} routes for zone ${targetIdStr}`);
                    }
                }

                // --- Sync Man Coverage Links ---
                const isOffense = target.data?.type === 'O';
                const connectedLinks = canvas.getObjects().filter(obj =>
                    obj.data?.type === 'man_coverage_link' &&
                    String(isOffense ? obj.data?.targetId : obj.data?.sourceId) === targetIdStr
                );

                connectedLinks.forEach(line => {
                    if (isOffense) line.set({ x2: centerPos.x, y2: centerPos.y });
                    else line.set({ x1: centerPos.x, y1: centerPos.y });
                    line.setCoords();
                    line.set({ dirty: true });
                });
            }

            canvas.requestRenderAll();
        }
    }, [updatePolylineShape]);



    onObjectMovingRef.current = onObjectMoving;

    // --- EFFECTS ---
    useEffect(() => {
        isEditModeRef.current = isEditMode;

        const canvas = fabricCanvas.current;
        if (!canvas) return;

        if (isEditMode) {
            // Enable Handles

            // STRICT: Always resolve route from the currently selected object
            // This prevents editing "stale" routes from previous selections
            let route = null;
            if (selectedObject) {
                const targetId = selectedObject.data?.id;
                const routes = canvas.getObjects().filter(o => o.data?.type === 'route_polyline' && o.data?.id === targetId);

                cleanupPolylineHandles();
                routes.forEach((route, idx) => {
                    refreshPolylineHandles(route, false);
                    // Lock the route line itself so it can't be dragged as a whole
                    route.selectable = false;
                    route.evented = false;
                });

                if (routes.length > 0) {
                    activePolyline.current = routes[routes.length - 1]; // Use latest for extension logic if needed
                }
            } else {
                cleanupPolylineHandles();
            }
        } else {
            // Disable Handles
            cleanupPolylineHandles();

            // Optional: Release activePolyline?
            // activePolyline.current = null; // Maybe keep it for reference?
        }
        canvas.requestRenderAll();
    }, [isEditMode, selectedObject, refreshPolylineHandles, cleanupPolylineHandles]);

    // Curve Mode Effect - Show blue joint handles when curve mode is enabled
    useEffect(() => {
        isCurveModeRef.current = isCurveMode;

        const canvas = fabricCanvas.current;
        if (!canvas) return;

        if (isCurveMode && selectedObject) {
            // Show curve handles for the selected object's routes
            const targetId = selectedObject.data?.id;
            const routes = canvas.getObjects().filter(o => o.data?.type === 'route_polyline' && o.data?.id === targetId);

            cleanupPolylineHandles();
            routes.forEach((route) => {
                refreshPolylineHandles(route, false);
            });
        } else if (!isCurveMode && !isEditMode) {
            // Cleanup handles if neither mode is active
            cleanupPolylineHandles();
        }
        canvas.requestRenderAll();
    }, [isCurveMode, selectedObject, isEditMode, refreshPolylineHandles, cleanupPolylineHandles]);

    // Initialize Canvas
    useEffect(() => {
        console.log("PlayDesigner v2.1 Loaded - Mobile Fullscreen");
        let isDisposed = false;

        const container = containerRef.current;
        const width = container?.clientWidth || window.innerWidth;
        const height = container?.clientHeight || window.innerHeight;

        const canvas = new fabric.Canvas(canvasRef.current, {
            width: width,
            height: height,
            backgroundColor: '#2D2D2D', // Match SVG background
            preserveObjectStacking: true,
            selection: false, // Handle selection manually for better touch exp
        });

        fabricCanvas.current = canvas;

        const drawField = async () => {
            try {
                // Remove existing background if switching modes
                if (canvas.backgroundImage) canvas.backgroundImage = null;

                const img = await fabric.Image.fromURL('/vertical-field.svg');
                if (isDisposed) return;

                const isFormation = designerMode === 'formation';
                // Adjust scale to fit usage
                // Formation mode focuses on LOS area

                // For Mobile Fullscreen, we want the field to fit width
                const scale = canvas.width / 700; // 700 is SVG internal width content excluding borders approx

                // Crop for Formation Mode
                const cropY = isFormation ? losYRef.current - 200 : 0;

                img.set({
                    originX: 'left',
                    originY: 'top',
                    selectable: false,
                    evented: false,
                    scaleX: canvas.width / 800, // SVG is 800 wide
                    scaleY: canvas.height / (isFormation ? FIELD_HEIGHT_FORMATION : 800),
                    top: isFormation ? -(losYRef.current - 150) * (canvas.height / FIELD_HEIGHT_FORMATION) : 0
                    // Simplified scaling logic for now, standard fullscreen fit
                });

                // Calculate scale
                const scaleX = canvas.width / 800;
                const scaleY = canvas.height / 800;
                const isMobile = canvas.width < 768;

                if (isMobile) {
                    // Mobile: stretch to fill width, field edges touch screen edges
                    img.set({
                        scaleX: scaleX,
                        scaleY: scaleX, // Use same scale to maintain aspect ratio but fill width
                        left: 0,
                        top: (canvas.height - (800 * scaleX)) / 2 // Center vertically
                    });
                } else {
                    // Desktop: original contain mode, centered
                    const fitScale = Math.min(scaleX, scaleY);
                    img.scale(fitScale);
                    img.set({
                        left: (canvas.width - img.getScaledWidth()) / 2,
                        top: (canvas.height - img.getScaledHeight()) / 2
                    });
                }

                canvas.backgroundImage = img;
                canvas.sendObjectToBack(img);

                if (initialData) {
                    const data = typeof initialData === 'string' ? JSON.parse(initialData) : initialData;

                    // 1. Analyze Saved Background Metrics (from loaded Play Data)
                    let savedBgLeft = 0, savedBgTop = 0, savedBgScaleX = 1, savedBgScaleY = 1;
                    const savedObjects = data.objects || [];
                    const bgObj = savedObjects.find(o => o.type === 'image' || o.type === 'Image');

                    if (bgObj) {
                        savedBgLeft = bgObj.left || 0;
                        savedBgTop = bgObj.top || 0;
                        savedBgScaleX = bgObj.scaleX || 1;
                        savedBgScaleY = bgObj.scaleY || 1;
                    }

                    // 2. Get Current Background Metrics (New Field just created)
                    const currentBgLeft = img.left || 0;
                    const currentBgTop = img.top || 0;
                    const currentBgScaleX = img.scaleX || 1;
                    const currentBgScaleY = img.scaleY || 1;

                    // 3. Calculate Field Scaling Ratio (Current Field / Saved Field)
                    // This ensures we scale objects based on the FIELD size, not the Screen size.
                    const fieldRatioX = currentBgScaleX / (savedBgScaleX || 1);
                    const fieldRatioY = currentBgScaleY / (savedBgScaleY || 1);

                    // 4. Filter Objects - Remove saved background and LOS
                    data.objects = savedObjects.filter(o => {
                        const t = o.type;
                        if (t === 'image' || t === 'Image') return false;
                        if (o.data && o.data.type === 'los') return false;
                        return true;
                    });

                    // 5. Load Objects using loadFromJSON (stable, no race conditions)
                    await canvas.loadFromJSON(data);

                    // 6. Transform Objects to Match New Field
                    // We use two passes: 1. Players (Anchors), 2. Everything else
                    const playerCenters = new Map();

                    // --- PASS 1: Players (Anchors) ---
                    canvas.getObjects().forEach(obj => {
                        if (obj === img) return;
                        const type = obj.data?.type;
                        if (type === 'O' || type === 'D') {
                            const relX = obj.left - savedBgLeft;
                            const relY = obj.top - savedBgTop;

                            const sX = (obj.scaleX || 1) * fieldRatioX;
                            const sY = (obj.scaleY || 1) * fieldRatioY;

                            const newLeft = currentBgLeft + (relX * fieldRatioX);
                            const newTop = currentBgTop + (relY * fieldRatioY);

                            obj.set({
                                left: newLeft,
                                top: newTop,
                                scaleX: sX,
                                scaleY: sY
                            });
                            obj.setCoords();

                            // Calculate Absolute Center based on origin
                            let cX = newLeft;
                            let cY = newTop;
                            if (obj.originX === 'left') cX += (obj.width * sX) / 2;
                            else if (obj.originX === 'right') cX -= (obj.width * sX) / 2;

                            if (obj.originY === 'top') cY += (obj.height * sY) / 2;
                            else if (obj.originY === 'bottom') cY -= (obj.height * sY) / 2;

                            if (obj.data?.id) {
                                const id = String(obj.data.id).trim();
                                playerCenters.set(id, { x: cX, y: cY });
                            }
                        }
                    });

                    // --- PASS 2: Routes and Others ---
                    canvas.getObjects().forEach(obj => {
                        if (obj === img) return;
                        if (obj.data?.type === 'O' || obj.data?.type === 'D') return;

                        const sX = (obj.scaleX || 1) * fieldRatioX;
                        const sY = (obj.scaleY || 1) * fieldRatioY;

                        if (obj.data?.type === 'route_polyline' && obj.points && obj.points.length > 0) {
                            // Link via ID or parentId
                            const id = String(obj.data.id || '').trim();
                            const parentId = String(obj.data.parentId || '').trim();
                            const anchor = playerCenters.get(id) || playerCenters.get(parentId);

                            if (anchor) {
                                // FIX: Transform points directly instead of using scale
                                // This prevents pathOffset calculation drift
                                const p0 = obj.points[0];
                                const po = obj.pathOffset || { x: 0, y: 0 };

                                // Calculate where first point currently is (absolute)
                                const oldP0AbsX = obj.left + (p0.x - po.x);
                                const oldP0AbsY = obj.top + (p0.y - po.y);

                                // Calculate delta to move first point to anchor
                                const deltaX = anchor.x - oldP0AbsX;
                                const deltaY = anchor.y - oldP0AbsY;

                                // Transform ALL points: scale them and shift by delta
                                obj.points = obj.points.map((pt, idx) => {
                                    // Scale relative to first point, then shift
                                    const scaledX = p0.x + (pt.x - p0.x) * fieldRatioX;
                                    const scaledY = p0.y + (pt.y - p0.y) * fieldRatioY;
                                    return new fabric.Point(scaledX, scaledY);
                                });

                                // Recalculate pathOffset after point transformation
                                obj._calcDimensions();

                                // Now position so new p0 aligns with anchor
                                const newP0 = obj.points[0];
                                const newPo = obj.pathOffset || { x: 0, y: 0 };

                                obj.set({
                                    left: anchor.x - (newP0.x - newPo.x),
                                    top: anchor.y - (newP0.y - newPo.y),
                                    scaleX: 1,
                                    scaleY: 1,
                                    originX: 'left',
                                    originY: 'top',
                                    fill: 'transparent',
                                    objectCaching: false
                                });
                            } else {
                                // Fallback: Transform points directly for non-anchored routes
                                const p0 = obj.points[0];
                                obj.points = obj.points.map(pt => {
                                    const scaledX = p0.x + (pt.x - p0.x) * fieldRatioX;
                                    const scaledY = p0.y + (pt.y - p0.y) * fieldRatioY;
                                    return new fabric.Point(scaledX, scaledY);
                                });
                                obj._calcDimensions();

                                const relX = obj.left - savedBgLeft;
                                const relY = obj.top - savedBgTop;
                                obj.set({
                                    left: currentBgLeft + (relX * fieldRatioX),
                                    top: currentBgTop + (relY * fieldRatioY),
                                    scaleX: 1,
                                    scaleY: 1
                                });
                            }
                        } else {
                            const relX = obj.left - savedBgLeft;
                            const relY = obj.top - savedBgTop;
                            obj.set({
                                left: currentBgLeft + (relX * fieldRatioX),
                                top: currentBgTop + (relY * fieldRatioY),
                                scaleX: sX,
                                scaleY: sY
                            });
                        }

                        obj.setCoords();
                    });

                    // 7. Restore Background
                    canvas.backgroundImage = img;
                }

                // CRITICAL: Check disposal before ANY rendering (covers both initialData and empty cases)
                if (isDisposed) return;

                // Draw LOS
                drawLOS(canvas);

                canvas.renderAll();
                // Save initial state to history for undo
                setHistory([canvas.toJSON(['data', 'pathOffset'])]);
            } catch (err) {
                console.error("Error drawing field:", err);
            }
        };

        drawField();

        // Event Listeners
        // Helper to apply glow
        const updateGlow = (obj, active) => {
            if (!obj || !obj.getObjects) return; // Only groups (players)



            const circle = obj.getObjects().find(o => o.type === 'circle');
            if (circle) {
                if (active) {
                    circle.set({
                        shadow: new fabric.Shadow({ color: '#a855f7', blur: 20, offsetX: 0, offsetY: 0 }),
                        stroke: '#a855f7'
                    });
                } else {
                    circle.set({
                        shadow: null,
                        stroke: 'white'
                    });
                }
                canvas.requestRenderAll();
            }
        };

        // Event Listeners
        canvas.on('selection:created', (e) => {
            if (e.selected) e.selected.forEach(obj => updateGlow(obj, true));

            const target = e.selected[0];
            const isHandle = target.data?.type === 'los_handle' ||
                target.data?.type === 'route_handle' ||
                target.data?.type === 'poly_handle' ||
                target.data?.type === 'curve_handle';

            if (!isHandle) {
                if (!isDisposed) {
                    setSelectedObject(target);
                    // Reset edit/curve modes when a new object (not a handle) is selected
                    setIsEditMode(false);
                    setIsCurveMode(false);
                }
            }
        });

        canvas.on('selection:updated', (e) => {
            // If we selected the handle, DON'T deselect the player conceptually
            const isHandle = e.selected && (
                e.selected[0].data?.type === 'route_handle' ||
                e.selected[0].data?.type === 'poly_handle' ||
                e.selected[0].data?.type === 'curve_handle'
            );
            if (isHandle) return;

            if (e.deselected) e.deselected.forEach(obj => {
                const type = obj.data?.type;
                if (type !== 'route_handle' && type !== 'poly_handle' && type !== 'curve_handle') updateGlow(obj, false);
            });

            // If switching to another object that isn't the handle, clear the handle
            cleanupPolylineHandles();

            if (e.selected) e.selected.forEach(obj => updateGlow(obj, true));
            if (!isDisposed) {
                setSelectedObject(e.selected[0]);
                // Reset edit/curve modes when switching players
                setIsEditMode(false);
                setIsCurveMode(false);
            }
        });

        canvas.on('selection:cleared', (e) => {
            if (drawingModeRef.current) return; // Maintain selection if drawing

            // If the thing we deselected was a handle, don't clear the CONCEPTUAL selection
            const wasHandle = e.deselected && e.deselected.some(obj => {
                const type = obj.data?.type;
                return type === 'poly_handle' || type === 'curve_handle' || type === 'route_handle';
            });

            if (wasHandle) return;

            if (e.deselected) e.deselected.forEach(obj => updateGlow(obj, false));
            cleanupPolylineHandles();

            if (!isDisposed) {
                setSelectedObject(null);
                // Reset modes when selection is cleared
                setIsEditMode(false);
                setIsCurveMode(false);
            }
        });

        // Dragging Logic - Use stable ref to avoid stale closures
        canvas.on('object:moving', (e) => onObjectMovingRef.current?.(e));
        canvas.on('object:scaling', (e) => onObjectMovingRef.current?.(e));
        canvas.on('object:modified', (e) => onObjectMovingRef.current?.(e));

        // Object Modified (Drag End) - Save history for ALL modifications
        canvas.on('object:modified', (e) => {
            // Restore visual controls after move
            if (e.target) {
                e.target.set({ hasBorders: true, hasControls: true });
            }
            saveToHistory();
        });

        // Double Click to Auto-Extend (or Create New)
        canvas.on('mouse:dblclick', (e) => {
            const target = e.target;
            // Only strictly allow double-click creation if NOT in Edit Mode
            if (target && target.data?.type === 'route_handle' && !isEditModeRef.current) {
                // console.log("Double Click on Handle: Auto-Extending Route - DISABLED");

                // Find previous segment to determine direction
                /*
                const segments = canvas.getObjects().filter(o =>
                    o.data?.type === 'route_segment' && o.data?.parentId == target.data.parentId
                );
    
                let dx = 0, dy = -50; // Default UP
    
                if (segments.length > 0) {
                    const lastSeg = segments[segments.length - 1];
                    // Direction vector
                    const vx = lastSeg.x2 - lastSeg.x1;
                    const vy = lastSeg.y2 - lastSeg.y1;
                    const len = Math.sqrt(vx * vx + vy * vy);
                    if (len > 0) {
                        // Normalize and scale to 50px
                        dx = (vx / len) * 50;
                        dy = (vy / len) * 50;
                    }
                    // Start from tip
                    routeSourcePoint.current = { x: lastSeg.x2, y: lastSeg.y2 };
                } else {
                    // Start from handle (player center)
                    routeSourcePoint.current = { x: target.left, y: target.top };
                }
    
                const start = routeSourcePoint.current;
                const end = { x: start.x + dx, y: start.y + dy };
    
                const lineId = Date.now().toString() + "_seg_dbl";
                const line = new fabric.Line([start.x, start.y, end.x, end.y], {
                    stroke: '#facc15', // Yellow
                    strokeWidth: 3,
                    selectable: false,
                    evented: false,
                    originX: 'center',
                    originY: 'center',
                    data: { type: 'route_segment', parentId: target.data?.parentId, id: lineId }
                });
    
                canvas.add(line);
                canvas.sendObjectToBack(line);
                if (canvas.backgroundImage) canvas.sendObjectToBack(canvas.backgroundImage);
    
                // Move handle to new tip
                target.left = end.x;
                target.top = end.y;
                target.setCoords(); // Important!
    
                // Reset State for next action
                activeRouteLine.current = null;
                routeSourcePoint.current = end;
                target.data.linkedLineId = null;
    
                canvas.renderAll();
                saveToHistory();
                */
            }
        });

        // Resize handler
        const handleResize = () => {
            const oldW = canvas.width;
            const oldH = canvas.height;

            const newW = containerRef.current?.clientWidth || window.innerWidth;
            const newH = containerRef.current?.clientHeight || window.innerHeight;

            // Calculate scale ratios
            const scaleX = newW / oldW;
            const scaleY = newH / oldH;

            canvas.setDimensions({ width: newW, height: newH });

            if (canvas.backgroundImage) {
                const img = canvas.backgroundImage;
                const bgScaleX = newW / 800;
                const bgScaleY = newH / 800;
                const isMobile = newW < 768;

                if (isMobile) {
                    // Mobile: fill width, field edges touch screen edges
                    img.set({
                        scaleX: bgScaleX,
                        scaleY: bgScaleX,
                        left: 0,
                        top: (newH - (800 * bgScaleX)) / 2
                    });
                } else {
                    // Desktop: original contain mode, centered
                    const fitScale = Math.min(bgScaleX, bgScaleY);
                    img.scale(fitScale);
                    img.set({
                        left: (newW - img.getScaledWidth()) / 2,
                        top: (newH - img.getScaledHeight()) / 2
                    });
                }
            }

            // Reposition all objects proportionally
            canvas.getObjects().forEach(obj => {
                // Skip background and LOS (they're redrawn separately)
                if (obj === canvas.backgroundImage || obj.data?.type === 'los_handle') return;

                // Scale position
                if (obj.left !== undefined) obj.set({ left: obj.left * scaleX });
                if (obj.top !== undefined) obj.set({ top: obj.top * scaleY });

                // Scale line coordinates if it's a line
                if (obj.type === 'line') {
                    obj.set({
                        x1: obj.x1 * scaleX,
                        y1: obj.y1 * scaleY,
                        x2: obj.x2 * scaleX,
                        y2: obj.y2 * scaleY
                    });
                }

                obj.setCoords();
            });

            drawLOS(canvas); // Re-draw LOS to fit
            canvas.renderAll();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            isDisposed = true;
            window.removeEventListener('resize', handleResize);
            if (canvas) {
                canvas.dispose();
            }
            fabricCanvas.current = null;
        };
    }, [initialData, designerMode]); // REMOVED losY from dependencies

    // Draw Advanced LOS
    const drawLOS = (canvas) => {
        // Clear old LOS
        if (losLineObj.current) canvas.remove(losLineObj.current);
        if (losHandleObj.current) canvas.remove(losHandleObj.current);

        // Calculate Y position relative to canvas
        const bg = canvas.backgroundImage;
        if (!bg) return;

        // Convert SVG Y (losY) to Canvas Y
        const canvasY = losYRef.current * bg.scaleY + bg.top;

        // Calculate field boundaries with padding to prevent overflow
        const fieldLeft = bg.left + 50; // Increased padding
        const fieldRight = (bg.left + bg.getScaledWidth()) - 50; // Increased padding

        const line = new fabric.Line([fieldLeft, canvasY, fieldRight, canvasY], {
            stroke: 'red',
            strokeWidth: 2,
            selectable: false,
            evented: false,
            opacity: 0.8,
            originY: 'center'
        });

        // Triangle Handle - Positioned at Left Edge
        const handle = new fabric.Triangle({
            width: 15, // Smaller handle
            height: 15,
            fill: 'red',
            left: fieldLeft, // Align exactly with line start
            top: canvasY,
            angle: 90,
            originX: 'center',
            originY: 'center',
            selectable: true,
            hasControls: false,
            hasBorders: false,
            lockMovementX: true, // Only vertical movement
            data: { type: 'los_handle' },
            hoverCursor: 'ns-resize',
            moveCursor: 'ns-resize'
        });

        let lastHandleY = canvasY;

        handle.on('moving', (e) => {
            const newY = handle.top;

            // Constrain to field bounds
            const minY = bg.top;
            const maxY = bg.top + bg.getScaledHeight();

            if (newY < minY) handle.top = minY;
            if (newY > maxY) handle.top = maxY;

            const constrainedY = handle.top;

            // Calculate delta
            const deltaY = constrainedY - lastHandleY;
            lastHandleY = constrainedY;

            // Move line with handle
            line.set({ y1: constrainedY, y2: constrainedY });

            // Move ALL other objects (Players, Routes, etc.)
            canvas.getObjects().forEach(obj => {
                // Skip exclusions
                if (obj === bg) return;
                if (obj === line) return;
                if (obj === handle) return;

                // Move it
                obj.set('top', obj.top + deltaY);
                obj.setCoords();
            });
        });

        handle.on('modified', (e) => {
            // Update State (losY) based on new position
            // Reverse Convert Canvas Y to SVG Y
            const newCanvasY = handle.top;
            const newLosY = (newCanvasY - bg.top) / bg.scaleY;
            setLosY(newLosY);
        });

        canvas.add(line, handle);
        canvas.bringObjectToFront(handle); // Ensure handle is on top
        losLineObj.current = line;
        losHandleObj.current = handle;
    };

    // --- GLOBAL DUPLICATE GUARDIAN ---
    // Watches for ANY zone addition and nukes collisions immediately
    useEffect(() => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;

        const onObjectAdded = (e) => {
            const newObj = e.target;
            if (!newObj || newObj.data?.type !== 'zone_coverage') return;

            // Check if this new object collides with any existing object
            const objects = canvas.getObjects();
            const collision = objects.find(o =>
                o !== newObj &&
                o !== canvas.backgroundImage &&
                o.data?.type === 'zone_coverage' &&
                Math.abs(o.left - newObj.left) < 5 &&
                Math.abs(o.top - newObj.top) < 5
            );

            if (collision) {
                // Duplicate detected!
                // Remove the OLDER one (collision) to keep the NEW one (active)
                // This assumes the new one is the one being dragged/created by the latest event
                canvas.remove(collision);
            }
        };

        canvas.on('object:added', onObjectAdded);
        return () => canvas.off('object:added', onObjectAdded);
    }, []);

    // --- DRAWING LOGIC (ZONES) ---
    useEffect(() => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;

        const handleMouseDown = (opt) => {
            // Only draw if in zone mode and not editing
            if (!drawingModeRef.current || !activeShapeType.current || isEditModeRef.current) return;

            // Prevent double-draw if listener fires twice or overlapped
            if (activeDrawingObject.current) return;

            // MUTEX LOCK: Use window to prevent multiple instances (React StrictMode etc)
            if (window.snappDrawingZoneMutex) return;
            window.snappDrawingZoneMutex = true;

            const pointer = canvas.getPointer(opt.e);
            const { x, y } = pointer;
            activeShapeStart.current = { x, y };
            connectionSourceRef.current = selectedObjectRef.current; // Snapshot selection for connection logic

            let shape;
            // Create shape with 0 dim initially
            if (activeShapeType.current === 'rect') {
                shape = new fabric.Rect({
                    left: x,
                    top: y,
                    width: 0,
                    height: 0,
                    fill: 'rgba(250, 204, 21, 0.2)', // yellow/20
                    stroke: '#facc15', // yellow
                    strokeWidth: 2,
                    selectable: true,
                    data: { type: 'zone_coverage', id: Date.now() }
                });
            } else if (activeShapeType.current === 'ellipse') {
                shape = new fabric.Ellipse({
                    left: x,
                    top: y,
                    rx: 0,
                    ry: 0,
                    fill: 'rgba(250, 204, 21, 0.2)',
                    stroke: '#facc15',
                    strokeWidth: 2,
                    selectable: true,
                    data: { type: 'zone_coverage', id: Date.now() }
                });
            }

            if (shape) {
                // Singleton Guard: Check if a shape with this ID was just added (extra safety)
                const exists = canvas.getObjects().find(o => o.data?.id === shape.data.id);
                if (!exists) {
                    canvas.discardActiveObject(); // Clear selection first
                    canvas.add(shape);
                    setSelectedObject(shape);
                    activeDrawingObject.current = shape;
                }
            }
        };

        const handleMouseMove = (opt) => {
            const shape = activeDrawingObject.current;
            if (!shape || !activeShapeStart.current) return;

            const pointer = canvas.getPointer(opt.e);
            const start = activeShapeStart.current;

            if (activeShapeType.current === 'rect') {
                const w = Math.abs(pointer.x - start.x);
                const h = Math.abs(pointer.y - start.y);
                const left = Math.min(pointer.x, start.x);
                const top = Math.min(pointer.y, start.y);

                shape.set({ width: w, height: h, left: left, top: top });
            } else if (activeShapeType.current === 'ellipse') {
                const rx = Math.abs(pointer.x - start.x) / 2;
                const ry = Math.abs(pointer.y - start.y) / 2;
                const left = Math.min(pointer.x, start.x);
                const top = Math.min(pointer.y, start.y);

                shape.set({ rx: rx, ry: ry, left: left, top: top });
            }
            canvas.requestRenderAll();
        };

        const handleMouseUp = () => {
            if (activeDrawingObject.current) {
                const shape = activeDrawingObject.current;
                shape.setCoords();

                // --- DEDUPLICATION: NUCLEAR OPTION ---
                // Remove ANY object that is virtually in the same place as the new shape
                // ignoring data checks in case the duplicate is malformed
                const objects = canvas.getObjects();
                const dups = objects.filter(o =>
                    o !== shape &&
                    o !== canvas.backgroundImage && // Don't delete background!
                    Math.abs(o.left - shape.left) < 2 &&
                    Math.abs(o.top - shape.top) < 2
                );

                if (dups.length > 0) {
                    dups.forEach(d => canvas.remove(d));
                    canvas.setActiveObject(shape);
                }
                // ------------------------------------------------

                // --- AUTOMATED ROUTE CONNECTION ---
                const selected = connectionSourceRef.current;
                if (selected && selected.data?.type === 'D') {
                    const playerId = selected.data.id;
                    shape.setCoords(); // Ensure latest coords for center calc
                    const zoneCenter = shape.getCenterPoint();

                    // Find existing route for this player
                    const existingRoute = canvas.getObjects().find(o =>
                        o.data?.type === 'route_polyline' && o.data?.id === playerId
                    );

                    if (existingRoute) {
                        // APPEND new point to existing route
                        const newIndex = existingRoute.points.length;
                        existingRoute.setCoords(); // Sync route
                        existingRoute.points.push(new fabric.Point(0, 0)); // Placeholder
                        updatePolylineShape(existingRoute, newIndex, zoneCenter);
                        existingRoute.set({
                            data: {
                                ...existingRoute.data,
                                connectedZoneId: String(shape.data.id),
                                endType: 'none'
                            }
                        });
                        updateRouteArrow(existingRoute, 'none'); // No arrow for zone
                    } else {
                        // Create new route from player center to zone center
                        const playerCenter = selected.getCenterPoint();
                        const poly = new fabric.Polyline([{ x: 0, y: 0 }], {
                            stroke: '#facc15',
                            strokeWidth: 4,
                            fill: 'transparent',
                            strokeLineCap: 'round',
                            strokeLineJoin: 'round',
                            left: playerCenter.x,
                            top: playerCenter.y,
                            objectCaching: false,
                            selectable: false,
                            evented: false,
                            originX: 'left',
                            originY: 'top',
                            data: {
                                type: 'route_polyline',
                                subType: 'route',
                                id: playerId,
                                connectedZoneId: String(shape.data.id),
                                endType: 'none'
                            }
                        });
                        canvas.add(poly);
                        // Use helper to place the 2nd point correctly
                        poly.points.push(new fabric.Point(0, 0));
                        updatePolylineShape(poly, 1, zoneCenter);
                        canvas.sendObjectToBack(poly);
                    }
                }

                saveToHistory();

                activeDrawingObject.current = null;
                activeShapeStart.current = null;
            }
            window.snappDrawingZoneMutex = false; // Release Global Lock
            reorderLayers(); // Ensure new shape goes to back, players to front
        };

        // Singleton Pattern: Prevent attaching duplicate listeners to the same canvas instance
        if (canvas.zoneListenersAttached) return;

        canvas.on('mouse:down', handleMouseDown);
        canvas.on('mouse:move', handleMouseMove);
        canvas.on('mouse:up', handleMouseUp);
        canvas.zoneListenersAttached = true;

        return () => {
            canvas.off('mouse:down', handleMouseDown);
            canvas.off('mouse:move', handleMouseMove);
            canvas.off('mouse:up', handleMouseUp);
            delete canvas.zoneListenersAttached;
        };
    }, [drawingMode, saveToHistory]);

    // --- HELPER: Layer Management ---
    const reorderLayers = useCallback(() => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;

        // 1. Send all Zones to back
        canvas.getObjects().forEach(obj => {
            if (obj.data?.type === 'zone_coverage') {
                canvas.sendObjectToBack(obj);
            }
        });

        // 2. FORCE Field Object to Very Back (behind Zones)
        // If the field is a regular object (not backgroundImage), it must be at index 0.
        const fieldObj = canvas.getObjects().find(o =>
            (o.type === 'image' || o.type === 'Image') &&
            o.width > 500 // Assumption: The Field is the largest image
        );
        if (fieldObj) {
            canvas.sendObjectToBack(fieldObj);
        }

        // 3. Bring Players, Routes, Handles to Front
        canvas.getObjects().forEach(obj => {
            const t = obj.data?.type;
            if (t === 'O' || t === 'D' || t === 'structure' ||
                t === 'route_polyline' || t === 'poly_handle' || t === 'curve_handle' ||
                t === 'route_arrow' || t === 'motion_arrow') {
                canvas.bringObjectToFront(obj);
            }
        });

        // 4. LOS Line always on very top
        if (losLineObj.current) canvas.bringObjectToFront(losLineObj.current);
        if (losHandleObj.current) canvas.bringObjectToFront(losHandleObj.current);

        canvas.requestRenderAll();
    }, []);

    // --- ACTIONS ---


    const undo = () => {
        if (history.length <= 1) return;
        const newHistory = [...history];
        newHistory.pop();
        const lastState = newHistory[newHistory.length - 1];
        setHistory(newHistory);

        // CRITICAL: Explicitly clear to prevent duplication
        fabricCanvas.current.clear();
        fabricCanvas.current.backgroundImage = null; // Direct assignment safer

        fabricCanvas.current.loadFromJSON(lastState).then(() => {
            const canvas = fabricCanvas.current;
            if (!canvas) return;

            // 1. Force absolute synchronization of all objects before any logic runs
            canvas.getObjects().forEach(obj => {
                obj.setCoords();
                // Ensure origin is correct for routes (just in case)
                if (obj.data?.type === 'route_polyline') {
                    obj.set({ originX: 'left', originY: 'top' });
                }
            });

            // 2. Initial Render to populate matrices
            canvas.renderAll();

            // 3. Re-link Background if it was lost in transfer
            const objects = canvas.getObjects();
            const potentialBg = objects.find(o =>
                (o.type === 'image' || o.type === 'Image') &&
                o.width > 500 &&
                o.selectable !== false
            );

            if (potentialBg) {
                canvas.remove(potentialBg);
                potentialBg.set({
                    selectable: false,
                    evented: false,
                    lockMovementX: true,
                    lockMovementY: true,
                    lockScalingX: true,
                    lockScalingY: true,
                    lockRotation: true,
                    hasControls: false,
                    hasBorders: false,
                    originX: 'left',
                    originY: 'top'
                });
                canvas.backgroundImage = potentialBg;
            }

            reorderLayers();

            // 4. Anchor Synchronization: Pin routes back to restored player/zone positions
            canvas.getObjects().forEach(obj => {
                if (obj.data?.type === 'route_polyline') {
                    // Sync Start (Player)
                    if (obj.data?.id) {
                        const playerId = String(obj.data.id);
                        const player = canvas.getObjects().find(p =>
                            (p.data?.type === 'O' || p.data?.type === 'D') &&
                            String(p.data?.id) === playerId
                        );

                        if (player && obj.points && obj.points.length > 0) {
                            const center = player.getCenterPoint();
                            updatePolylineShape(obj, 0, center);
                        }
                    }

                    // Sync End (Zone)
                    if (obj.data?.connectedZoneId) {
                        const zoneId = String(obj.data.connectedZoneId);
                        const zone = canvas.getObjects().find(z =>
                            z.data?.type === 'zone_coverage' &&
                            String(z.data?.id) === zoneId
                        );
                        if (zone && obj.points && obj.points.length > 1) {
                            const center = zone.getCenterPoint();
                            updatePolylineShape(obj, obj.points.length - 1, center);
                        }
                    }

                    obj.setCoords();
                    obj.set({ dirty: true });
                    updateRouteArrow(obj);
                }
            });

            // Final sync for Man Coverage links
            canvas.getObjects().forEach(obj => {
                if (obj.data?.type === 'man_coverage_link') {
                    const src = canvas.getObjects().find(o => String(o.data?.id) === String(obj.data.sourceId));
                    const dst = canvas.getObjects().find(o => String(o.data?.id) === String(obj.data.targetId));
                    if (src && dst) {
                        const cp1 = src.getCenterPoint();
                        const cp2 = dst.getCenterPoint();
                        obj.set({ x1: cp1.x, y1: cp1.y, x2: cp2.x, y2: cp2.y });
                    }
                }
            });

            canvas.renderAll();
            drawLOS(canvas);
        });

    };

    const resetPlay = useCallback(() => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;

        setIsPlaying(false);
        isPlayingRef.current = false;

        // Restore original positions
        canvas.getObjects().forEach(obj => {
            // ONLY restore players (O/D) to avoid ID collision with routes sharing the same ID
            if ((obj.data?.type === 'O' || obj.data?.type === 'D') && obj.data?.id && originalPositions.current[obj.data.id]) {
                const pos = originalPositions.current[obj.data.id];
                obj.set({ left: pos.left, top: pos.top });
                obj.setCoords();
            }
        });

        // Sync routes after reset to ensure they are connected to player's center
        canvas.getObjects().forEach(obj => {
            if (obj.data?.type === 'O' || obj.data?.type === 'D') {
                const targetId = obj.data?.id;
                const routes = canvas.getObjects().filter(o => o.data?.type === 'route_polyline' && o.data?.id === targetId);
                routes.forEach(poly => {
                    // Update the start point of the route to match the player's center
                    const center = obj.getCenterPoint();
                    if (poly.data?.subType === 'motion') {
                        // Motion starts at player
                        updatePolylineShape(poly, 0, center);
                    } else {
                        // Regular route starts at player if NO motion, otherwise starts at motion tip
                        // (Extension logic usually handles this, but a full sync here is safer)
                        const hasMotion = routes.some(r => r.data?.subType === 'motion' && r !== poly);
                        if (!hasMotion) {
                            updatePolylineShape(poly, 0, center);
                        }
                    }
                });
            }
        });

        // NOTE: Redundant route syncing removed as it caused coordinate drift
        canvas.renderAll();
    }, [updatePolylineShape]);

    const runPlay = useCallback(() => {
        const canvas = fabricCanvas.current;
        if (!canvas || isPlaying) return;

        // CRITICAL: Discard selection before capturing positions
        canvas.discardActiveObject();
        canvas.requestRenderAll();

        setIsPlaying(true);
        isPlayingRef.current = true;
        cleanupPolylineHandles();
        setSelectedObject(null);

        // Bring all players to front so they are above routes and decorations
        canvas.getObjects().forEach(obj => {
            if (obj.data?.type === 'O' || obj.data?.type === 'D') {
                canvas.bringObjectToFront(obj);
            }
        });

        // Store original positions (now guaranteed to be canvas-relative)
        const newOriginals = {};
        canvas.getObjects().forEach(obj => {
            if (obj.data?.type === 'O' || obj.data?.type === 'D') {
                newOriginals[obj.data.id] = { left: obj.left, top: obj.top };
            }
        });
        originalPositions.current = newOriginals;

        const players = canvas.getObjects().filter(obj => obj.data?.type === 'O' || obj.data?.type === 'D');

        // Animation Helper (Returns Promise)
        const animateAlongPoints = (player, allPoints) => {
            return new Promise((resolve) => {
                if (!allPoints || allPoints.length === 0) {
                    resolve();
                    return;
                }

                let currentPtIdx = 0;
                const animateNextSegment = () => {
                    if (currentPtIdx >= allPoints.length || !isPlayingRef.current) {
                        resolve();
                        return;
                    }

                    const nextPt = allPoints[currentPtIdx];
                    const dx = nextPt.x - player.left;
                    const dy = nextPt.y - player.top;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    // Base Speed: 450ms per 100px (Slower than turbo, faster than default)
                    // OL Speed: 800ms per 100px (Considerably slower)
                    const isOL = ['C', 'LT', 'RT', 'LG', 'RG'].includes(player.data?.role);
                    const speedPer100px = isOL ? 800 : 450;
                    const segmentDuration = (dist / 100) * speedPer100px;

                    const startX = player.left;
                    const startY = player.top;

                    fabric.util.animate({
                        startValue: 0,
                        endValue: 1,
                        duration: segmentDuration,
                        easing: fabric.util.ease.linear,
                        onChange: (value) => {
                            if (!isPlayingRef.current) return;
                            const radius = player.data?.radius || 14;
                            player.set({
                                left: startX + (nextPt.x - radius - startX) * value,
                                top: startY + (nextPt.y - radius - startY) * value
                            });
                        },
                        onComplete: () => {
                            if (!isPlayingRef.current) {
                                resolve();
                                return;
                            }
                            currentPtIdx++;
                            animateNextSegment();
                        }
                    });
                };
                animateNextSegment();
            });
        };

        // Prepare points for all players
        const playerPaths = players.map(player => {
            player.set({ objectCaching: false });
            const playerId = player.data.id;
            const allRoutes = canvas.getObjects().filter(obj =>
                obj.data?.type === 'route_polyline' && obj.data?.id === playerId
            );

            const motionRoutes = allRoutes.filter(r => r.data?.subType === 'motion');
            const regularRoutes = allRoutes.filter(r => r.data?.subType === 'route');

            const getPointsFromRoutes = (routes, startFromPoints = []) => {
                let pts = [...startFromPoints];
                routes.forEach(poly => {
                    const matrix = poly.calcTransformMatrix();
                    const offset = poly.pathOffset;
                    const p = poly.points;
                    const curvedPoints = poly.data?.curvedPoints || {};
                    const globalCurved = poly.data?.isCurved;
                    const getAbs = (pt) => fabric.util.transformPoint({ x: pt.x - offset.x, y: pt.y - offset.y }, matrix);

                    if ((globalCurved || Object.values(curvedPoints).some(v => v)) && p.length > 2) {
                        // Curved Sampling
                        if (pts.length === 0) pts.push(getAbs(p[0]));
                        for (let i = 1; i < p.length - 1; i++) {
                            const isCurved = globalCurved || curvedPoints[i];
                            const xc = (p[i].x + p[i + 1].x) / 2;
                            const yc = (p[i].y + p[i + 1].y) / 2;
                            if (isCurved) {
                                const p0 = pts[pts.length - 1];
                                const p1 = getAbs(p[i]);
                                const p2 = getAbs({ x: xc, y: yc });
                                const samples = 4; // Minimal samples to eliminate micro-animation overhead
                                for (let t = 1 / samples; t <= 1; t += 1 / samples) {
                                    const bX = Math.pow(1 - t, 2) * p0.x + 2 * (1 - t) * t * p1.x + Math.pow(t, 2) * p2.x;
                                    const bY = Math.pow(1 - t, 2) * p0.y + 2 * (1 - t) * t * p1.y + Math.pow(t, 2) * p2.y;
                                    pts.push({ x: bX, y: bY });
                                }
                            } else {
                                pts.push(getAbs(p[i]));
                                pts.push(getAbs({ x: xc, y: yc }));
                            }
                        }
                        pts.push(getAbs(p[p.length - 1]));
                    } else {
                        const startIdx = pts.length > 0 ? 1 : 0;
                        for (let i = startIdx; i < p.length; i++) {
                            pts.push(getAbs(p[i]));
                        }
                    }
                });
                return pts;
            };

            return {
                player,
                motionPoints: getPointsFromRoutes(motionRoutes),
                routePoints: getPointsFromRoutes(regularRoutes)
            };
        });

        const startPhases = async () => {
            // PHASE 1: Motion (Pre-Snap)
            await Promise.all(playerPaths.map(pp => animateAlongPoints(pp.player, pp.motionPoints)));

            if (!isPlayingRef.current) return;

            // PHASE 2: Route (Post-Snap)
            // Note: We don't await this if we want it to just run, 
            // but for "complete play designer" state it's better to await or just let it finish.
            // We'll run them all in parallel.
            Promise.all(playerPaths.map(pp => animateAlongPoints(pp.player, pp.routePoints)));
        };

        startPhases();
    }, [isPlaying, cleanupPolylineHandles, updatePolylineShape]);

    const addPlayer = (role) => {
        if (!fabricCanvas.current) return;

        const mainType = playbookType === 'defense' ? 'D' : 'O';
        const defaultRole = role || (mainType === 'O' ? 'WR' : 'CB'); // Default role based on type

        // Find center of screen
        const center = fabricCanvas.current.getCenter();
        const player = createPlayerObject(mainType, defaultRole, 400, losY);

        // Manual Placement Logic
        // If Defense Playbook (Main=D): User wants them at TOP (Negative).
        // If Offense Playbook (Main=O): User wants them at BOTTOM (Positive).
        const isDefensePlaybook = playbookType === 'defense';
        const offset = isDefensePlaybook ? -50 : 50;

        player.set({
            left: center.left,
            top: (losYRef.current || center.top) + offset
        });

        fabricCanvas.current.add(player);
        fabricCanvas.current.setActiveObject(player);
        saveToHistory();
    };

    const addOpponent = () => {
        if (!fabricCanvas.current) return;

        const oppType = playbookType === 'defense' ? 'O' : 'D';
        const defaultRole = oppType === 'O' ? 'WR' : 'CB';

        const center = fabricCanvas.current.getCenter();
        // Opponent always at Top (Negative relative to center/LOS)
        // If Playbook=Defense, User=Bottom, Opp=Top.
        // If Playbook=Offense, User=Bottom, Opp=Top.
        const player = createPlayerObject(oppType, defaultRole, 400, losY);

        // Opponent Placement
        // If Defense Playbook (Opp=O): Place Bottom (Positive).
        // If Offense Playbook (Opp=D): Place Top (Negative).
        const isDefensePlaybook = playbookType === 'defense';
        const offset = isDefensePlaybook ? 50 : -50;

        player.set({
            left: center.left + 50,
            top: (losYRef.current || center.top) + offset
        });

        fabricCanvas.current.add(player);
        saveToHistory();
    };

    // Updated Player creation with Glow
    const createPlayerObject = (type, role, svgX, svgY) => {
        const color = type === 'O' ? '#3b82f6' : '#ef4444';

        // Responsive player size - smaller on mobile
        const canvas = fabricCanvas.current;
        const isMobile = canvas && canvas.width < 768;
        const playerRadius = isMobile ? 10 : 14;
        const fontSize = isMobile ? 9 : 12;

        const circle = new fabric.Circle({
            radius: playerRadius,
            fill: color,
            stroke: 'white',
            strokeWidth: 2,
            originX: 'center',
            originY: 'center',
        });

        // Purple Glow for Selection (Added as invisible circle that becomes visible on select?)
        // Or simpler: handle selection styling in selection:created event

        const text = new fabric.Text(role || type, {
            fontSize: fontSize,
            fontFamily: 'Assistant, sans-serif',
            fontWeight: 'bold',
            fill: 'white',
            originX: 'center',
            originY: 'center',
        });

        // Group
        const group = new fabric.Group([circle, text], {
            // Position calc deferred to caller or formation logic
            hasControls: false,
            hasBorders: false, // No square selection box
            padding: 5,
            lockMovementX: !isMoveMode,
            lockMovementY: !isMoveMode,
            hoverCursor: isMoveMode ? 'move' : 'default',
            data: { type, role, radius: playerRadius, id: Date.now().toString() + Math.random() }
        });

        return group;
    };

    const addFormation = (key) => {
        const isDefense = playbookType === 'defense';
        const formationSet = isDefense ? DEFENSE_FORMATIONS : FORMATION_OFFSETS;
        const formation = { ...formationSet, ...customFormations }[key];

        if (!formation) return;

        const canvas = fabricCanvas.current;
        const targetType = isDefense ? 'D' : 'O';

        // Clear existing players of the target type
        canvas.getObjects().forEach(obj => {
            if (obj.data?.type === targetType) {
                // Remove associated routes
                const routes = canvas.getObjects().filter(r => r.data?.type === 'route_polyline' && r.data?.id === obj.data?.id);
                routes.forEach(r => canvas.remove(r));
                canvas.remove(obj);
            }
        });

        const bg = canvas.backgroundImage;
        const bgScale = bg ? bg.scaleX : 1;

        const canvasCenterX = canvas.width / 2;
        // Use the actual LOS handle position on canvas (same reference as player constraint logic line 1167)
        const currentLosY = losHandleObj.current ? losHandleObj.current.top : (canvas.height / 2);

        console.log('[addFormation] DEBUG:', {
            losHandleExists: !!losHandleObj.current,
            losHandleTop: losHandleObj.current?.top,
            currentLosY,
            canvasHeight: canvas.height,
            bgScale,
            YARD_PX
        });

        formation.forEach(p => {
            const pObj = createPlayerObject(targetType, p.role);

            // X placement - already in pixels
            const pixelX = canvasCenterX + (p.x * bgScale);

            // Y placement
            // IMPORTANT: Defense formations are defined with PIXEL offsets (e.g. -40 means 40px above LOS)
            // Offense formations are defined with YARD offsets (e.g. 5 means 5 yards below LOS)
            // So we multiply by YARD_PX only for offense
            let pixelY;
            if (isDefense) {
                // Defense: Y values are already in pixels, just scale with background
                pixelY = currentLosY + (p.y * bgScale);
            } else {
                // Offense: Y values are in yards, convert to pixels
                pixelY = currentLosY + (p.y * YARD_PX * bgScale);
            }
            console.log(`[addFormation] Player ${p.role}: p.y=${p.y}, pixelY=${pixelY}, isDefense=${isDefense}`);

            pObj.set({
                left: pixelX,
                top: pixelY
            });

            canvas.add(pObj);
        });

        canvas.requestRenderAll();

        // Reset history so this formation becomes the new baseline
        // User cannot "undo" to an empty field after selecting a formation
        setHistory([]);
        saveToHistory();
    };

    // --- CONTEXTUAL TOOLS & DRAWING ---

    // --- ZONE SHAPE DRAWING EVENTS ---
    const onZoneMouseDown = useCallback((opt) => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;
        const ptr = canvas.getPointer(opt.e);
        activeShapeStart.current = { x: ptr.x, y: ptr.y };

        const fill = 'rgba(251, 191, 36, 0.35)';
        const stroke = '#fbbf24';

        let shape;
        if (activeShapeType.current === 'rect') {
            shape = new fabric.Rect({
                left: ptr.x,
                top: ptr.y,
                width: 0,
                height: 0,
                fill: fill,
                stroke: stroke,
                strokeWidth: 2,
                selectable: false,
                evented: false,
                objectCaching: false,
                data: { type: 'zone_coverage', shape: 'rect', id: Date.now().toString() }
            });
        } else if (activeShapeType.current === 'ellipse') {
            shape = new fabric.Ellipse({
                left: ptr.x,
                top: ptr.y,
                rx: 0,
                ry: 0,
                fill: fill,
                stroke: stroke,
                strokeWidth: 2,
                selectable: false,
                evented: false,
                objectCaching: false,
                originX: 'center',
                originY: 'center',
                data: { type: 'zone_coverage', shape: 'ellipse', id: Date.now().toString() }
            });
        }

        if (shape) {
            canvas.add(shape);
            activePolyline.current = shape; // Reuse activePolyline ref
        }
    }, []);

    const onZoneMouseMove = useCallback((opt) => {
        if (!activePolyline.current || !activeShapeStart.current) return;
        const canvas = fabricCanvas.current;
        const ptr = canvas.getPointer(opt.e);
        const start = activeShapeStart.current;
        const shape = activePolyline.current;

        if (activeShapeType.current === 'rect') {
            const width = Math.abs(ptr.x - start.x);
            const height = Math.abs(ptr.y - start.y);
            const left = Math.min(ptr.x, start.x);
            const top = Math.min(ptr.y, start.y);
            shape.set({ width, height, left, top, originX: 'left', originY: 'top' });
        } else if (activeShapeType.current === 'ellipse') {
            const rx = Math.abs(ptr.x - start.x) / 2;
            const ry = Math.abs(ptr.y - start.y) / 2;
            const cx = Math.min(ptr.x, start.x) + rx;
            const cy = Math.min(ptr.y, start.y) + ry;
            shape.set({ rx, ry, left: cx, top: cy });
        }
        canvas.requestRenderAll();
    }, []);

    const onZoneMouseUp = useCallback(() => {
        const canvas = fabricCanvas.current;
        const shape = activePolyline.current;

        if (shape) {
            // Validate size
            if ((shape.width < 5 && shape.height < 5) && (shape.rx < 3 && shape.ry < 3)) {
                canvas.remove(shape);
            } else {
                shape.set({
                    selectable: true,
                    evented: true,
                    objectCaching: false,
                    hasBorders: false, // Hide frame
                    hasControls: false, // Hide handles (dots)
                    cornerColor: 'white',
                    cornerStrokeColor: '#fbbf24', // Gold
                    cornerSize: 8,
                    transparentCorners: false,
                    cornerStyle: 'circle'
                });
                canvas.setActiveObject(shape);

                // --- AUTOMATED ROUTE CONNECTION ---
                const selected = connectionSourceRef.current;
                if (selected && (selected.data?.type === 'D' || selected.data?.type === 'O')) {
                    const playerId = String(selected.data.id).trim();
                    shape.setCoords();
                    const zoneCenter = shape.getCenterPoint();

                    const allObjects = canvas.getObjects();

                    // Filter: Only standard routes (not ends, not links)
                    const playerRoutes = allObjects.filter(o => {
                        if (o.data?.type !== 'route_polyline') return false;
                        const oid = String(o.data?.id || '').trim();
                        const opid = String(o.data?.parentId || '').trim();
                        return oid === playerId || opid === playerId;
                    });

                    if (playerRoutes.length > 0) {
                        // Snapping existing. 
                        // DEDUPLICATE: If more than 1 route found, keep the first one, remove others?
                        // No, player might have Route + Motion. Keep both!
                        playerRoutes.forEach(existingRoute => {
                            existingRoute.setCoords();
                            const lastIndex = existingRoute.points.length - 1;

                            if (existingRoute.points.length > 1) {
                                updatePolylineShape(existingRoute, lastIndex, zoneCenter);
                            } else {
                                existingRoute.points.push(new fabric.Point(0, 0));
                                updatePolylineShape(existingRoute, 1, zoneCenter);
                            }

                            existingRoute.set({
                                data: {
                                    ...existingRoute.data,
                                    connectedZoneId: String(shape.data.id),
                                    endType: 'none'
                                }
                            });
                            updateRouteArrow(existingRoute, 'none');
                        });
                        removeDuplicateRoutes(playerId);
                    } else {
                        // Truly no routes found. Create one.
                        const playerCenter = selected.getCenterPoint();
                        const poly = new fabric.Polyline([{ x: 0, y: 0 }], {
                            stroke: '#facc15',
                            strokeWidth: 4,
                            fill: 'transparent',
                            strokeLineCap: 'round',
                            strokeLineJoin: 'round',
                            left: playerCenter.x,
                            top: playerCenter.y,
                            objectCaching: false,
                            selectable: false,
                            evented: false,
                            originX: 'left',
                            originY: 'top',
                            data: {
                                type: 'route_polyline',
                                subType: 'route',
                                id: playerId,
                                connectedZoneId: String(shape.data.id),
                                endType: 'none'
                            }
                        });
                        canvas.add(poly);
                        poly.points.push(new fabric.Point(0, 0));
                        updatePolylineShape(poly, 1, zoneCenter);
                        canvas.sendObjectToBack(poly);
                    }
                }

                saveToHistory();
            }
        }

        activePolyline.current = null;
        activeShapeStart.current = null;
        activeShapeType.current = null;

        // Cleanup listeners
        canvas.off('mouse:down', onZoneMouseDown);

        canvas.off('mouse:move', onZoneMouseMove);
        canvas.off('mouse:up', onZoneMouseUp);

        setDrawingMode(null);
    }, [saveToHistory]);

    const addZoneShape = (type) => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;

        // CRITICAL: Finish any active drawing to prevent orphaned preview lines
        if (drawingModeRef.current) {
            finishDrawing();
        }

        // Set mode
        setDrawingMode('zone_shape');
        drawingModeRef.current = 'zone_shape'; // Sync IMMEDIATELY to prevent selection clear
        connectionSourceRef.current = selectedObjectRef.current; // Snapshot selection
        activeShapeType.current = type;
        setShowZoneMenu(false);

        // Attach listeners
        canvas.on('mouse:down', onZoneMouseDown);
        canvas.on('mouse:move', onZoneMouseMove);
        canvas.on('mouse:up', onZoneMouseUp);

        activeRouteHandlers.current.zone_down = onZoneMouseDown;
        activeRouteHandlers.current.zone_move = onZoneMouseMove;
        activeRouteHandlers.current.zone_up = onZoneMouseUp;
        activeRouteHandlers.current.zone = true;

        // Deselect others
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        return;

        // Old Logic (Dead Code)


        let shape;

        if (type === 'rect') {
            shape = new fabric.Rect({
                left: center.left,
                top: center.top,
                fill: fill,
                stroke: stroke,
                strokeWidth: 2,
                width: 150,
                height: 100,
                originX: 'center',
                originY: 'center',
                data: { type: 'zone_coverage', shape: 'rect', id: Date.now().toString() }
            });
        } else if (type === 'ellipse') {
            shape = new fabric.Ellipse({
                left: center.left,
                top: center.top,
                fill: fill,
                stroke: stroke,
                strokeWidth: 2,
                rx: 80,
                ry: 50,
                originX: 'center',
                originY: 'center',
                data: { type: 'zone_coverage', shape: 'ellipse', id: Date.now().toString() }
            });
        }
        if (shape) {
            canvas.add(shape);
            canvas.setActiveObject(shape);
            canvas.requestRenderAll();
            saveToHistory();
            setShowZoneMenu(false);
        }
    };

    const addOffensivePlayer = useCallback(() => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;

        // Default position: Center(ish) or Viewport Center
        // Let's put them near the QB spot but slightly offset
        const startX = 400;
        const startY = losYRef.current + 50;

        // Create Circle 'O'
        const player = new fabric.Circle({
            radius: 14,
            fill: '#ffffff',
            stroke: '#0ea5e9', // Sky Blue
            strokeWidth: 2,
            left: startX,
            top: startY,
            originX: 'center',
            originY: 'center',
            hasControls: false,
            hasBorders: true,
            borderColor: '#0ea5e9',
            padding: 5,
            data: { type: 'O', id: Date.now().toString(), role: 'O' } // Generic Role
        });

        canvas.add(player);
        canvas.setActiveObject(player);
        canvas.requestRenderAll();
        saveToHistory();
    }, [saveToHistory]);

    const finishDrawing = useCallback(() => {
        if (!fabricCanvas.current) return;
        const canvas = fabricCanvas.current;
        const currentMode = drawingModeRef.current || drawingMode;

        // --- GLOBAL CLEANUP for all drawing tools ---
        canvas.off('mouse:down', onZoneMouseDown);
        canvas.off('mouse:move', onZoneMouseMove);
        canvas.off('mouse:up', onZoneMouseUp);

        const { move, down, zone_down, zone_move, zone_up } = activeRouteHandlers.current;
        if (move) canvas.off('mouse:move', move);
        if (down) canvas.off('mouse:down', down);
        if (zone_move) canvas.off('mouse:move', zone_move);
        if (zone_down) canvas.off('mouse:down', zone_down);
        if (zone_up) canvas.off('mouse:up', zone_up);
        canvas.off('mouse:dblclick', finishDrawing);

        activeRouteHandlers.current = { move: null, down: null, zone: false, zone_down: null, zone_move: null, zone_up: null };
        cleanupPreviewLine();

        // 0. CLEANUP for internal shape-based tools (Rect/Ellipse)
        if (currentMode === 'zone_shape') {
            activeShape.current = null;
            setDrawingMode(null);
            drawingModeRef.current = null;
            connectionSourceRef.current = null;
            canvas.selection = true;
            canvas.defaultCursor = 'default';
            canvas.requestRenderAll();
            return;
        }

        // 1. ZONE LOGIC (Polygon)
        if (currentMode === 'zone' && activePolyline.current) {
            const points = activePolyline.current.points;
            if (points.length > 2) {
                const polygonDataId = Date.now().toString();
                const polygon = new fabric.Polygon(points, {
                    fill: 'rgba(255, 215, 0, 0.35)', // Gold
                    stroke: '#fbbf24',
                    strokeWidth: 2,
                    selectable: true,
                    evented: true,
                    objectCaching: false,
                    originX: 'center',
                    originY: 'center',
                    data: { type: 'zone_coverage', id: polygonDataId }
                });
                canvas.add(polygon);
                canvas.setActiveObject(polygon);

                // --- AUTOMATED ROUTE CONNECTION for Polygon Zones ---
                const selected = connectionSourceRef.current;
                if (selected && (selected.data?.type === 'D' || selected.data?.type === 'O')) {
                    const playerId = String(selected.data.id).trim();
                    polygon.setCoords();
                    const zoneCenter = polygon.getCenterPoint();

                    const allObjects = canvas.getObjects();
                    const playerRoutes = allObjects.filter(o => {
                        if (o.data?.type !== 'route_polyline') return false;
                        const oid = String(o.data?.id || '').trim();
                        const opid = String(o.data?.parentId || '').trim();
                        return oid === playerId || opid === playerId;
                    });

                    if (playerRoutes.length > 0) {
                        playerRoutes.forEach(existingRoute => {
                            existingRoute.setCoords();
                            const lastIndex = existingRoute.points.length - 1;
                            if (existingRoute.points.length > 1) {
                                updatePolylineShape(existingRoute, lastIndex, zoneCenter);
                            } else {
                                existingRoute.points.push(new fabric.Point(0, 0));
                                updatePolylineShape(existingRoute, 1, zoneCenter);
                            }
                            existingRoute.set({
                                data: { ...existingRoute.data, connectedZoneId: String(polygon.data.id), endType: 'none' }
                            });
                            updateRouteArrow(existingRoute, 'none');
                        });
                        removeDuplicateRoutes(playerId);
                    } else {
                        const playerCenter = selected.getCenterPoint();
                        const poly = new fabric.Polyline([{ x: 0, y: 0 }], {
                            stroke: '#facc15',
                            strokeWidth: 4,
                            fill: 'transparent',
                            strokeLineCap: 'round',
                            strokeLineJoin: 'round',
                            left: playerCenter.x,
                            top: playerCenter.y,
                            objectCaching: false,
                            selectable: false,
                            evented: false,
                            originX: 'left',
                            originY: 'top',
                            data: {
                                type: 'route_polyline',
                                subType: 'route',
                                id: playerId,
                                connectedZoneId: String(polygon.data.id),
                                endType: 'none'
                            }
                        });
                        canvas.add(poly);
                        poly.points.push(new fabric.Point(0, 0));
                        updatePolylineShape(poly, 1, zoneCenter);
                        canvas.sendObjectToBack(poly);
                    }
                }
            }
            if (activePolyline.current) canvas.remove(activePolyline.current);
            activePolyline.current = null;
            previewLine.current = null;
            activeShape.current = null; // Ensure activeShape is cleared for zone polygons

            setDrawingMode(null);
            drawingModeRef.current = null;
            connectionSourceRef.current = null;
            saveToHistory();
            canvas.requestRenderAll();
            return;
        }

        // 2. ROUTE/MOTION LOGIC
        if (activePolyline.current) {
            if (playbookType === 'defense') {
                activePolyline.current.data.isCurved = true;
            }
            updateRouteArrow(activePolyline.current);

            // CRITICAL: Release the polyline
            activePolyline.current = null;

            setDrawingMode(null);
            drawingModeRef.current = null;
            connectionSourceRef.current = null;
            canvas.selection = true;
            canvas.defaultCursor = 'default';
            canvas.renderAll();
            saveToHistory();
        }
    }, [cleanupPreviewLine, updateRouteArrow, saveToHistory, drawingMode, playbookType]);

    // --- MAN COVERAGE HANDLER ---
    const onManCoverageMouseDown = useCallback((opt) => {
        const canvas = fabricCanvas.current;
        // MUST be a player source
        const isPlayerSource = selectedObject?.data?.type === 'O' || selectedObject?.data?.type === 'D';
        if (!canvas || !selectedObject || !isPlayerSource) return;

        const target = opt.target;
        if (target && target.data?.type === 'O') {
            // Found target! Create Link.
            const sourceId = selectedObject.data.id;
            const targetId = target.data.id;

            // Enforce One-to-One: Remove existing link for this source
            const existingLinks = canvas.getObjects().filter(o =>
                o.data?.type === 'man_coverage_link' && String(o.data?.sourceId) === String(sourceId)
            );
            if (existingLinks.length > 0) {
                canvas.remove(...existingLinks);
            }

            const start = selectedObject.getCenterPoint();
            const end = target.getCenterPoint();

            const linkLine = new fabric.Line([start.x, start.y, end.x, end.y], {
                stroke: '#ef4444', // Red
                strokeWidth: 2,
                strokeDashArray: [5, 5],
                selectable: true,
                evented: true,
                originX: 'center',
                originY: 'center',
                data: {
                    type: 'man_coverage_link',
                    sourceId: String(sourceId),
                    targetId: String(targetId)
                }
            });

            canvas.add(linkLine);
            canvas.sendObjectToBack(linkLine);
            if (canvas.backgroundImage) canvas.sendObjectToBack(canvas.backgroundImage);

            // Exit mode
            setDrawingMode(null);
            canvas.defaultCursor = 'default';
            // canvas.selection = true; // Re-enable selection?

            // Re-select the defender?
            canvas.setActiveObject(selectedObject);

            saveToHistory();
        }
    }, [selectedObject, saveToHistory]);

    const startDrawing = (drawType) => {
        // Allow 'zone' to start without selected object (Free Drawing)
        const isPlayer = selectedObject?.data?.type === 'O' || selectedObject?.data?.type === 'D';
        if (drawType !== 'zone' && (!selectedObject || !fabricCanvas.current || !isPlayer)) return;

        // Toggle off if clicking same tool
        if (drawingMode === drawType) {
            finishDrawing();
            return;
        }

        // CRITICAL: Finish any active drawing (including switch from route to motion)
        if (drawingMode) {
            finishDrawing();
        }

        // Mutual Exclusivity: Turn off other modes
        setIsEditMode(false);
        setIsCurveMode(false);
        isEditModeRef.current = false;
        isCurveModeRef.current = false;

        setDrawingMode(drawType);
        drawingModeRef.current = drawType; // Sync IMMEDIATELY
        connectionSourceRef.current = selectedObjectRef.current; // Snapshot selection
        fabricCanvas.current.selection = false;

        // --- ROUTE & MOTION DRAWING (POLYLINE) ---
        if (drawType === 'route' || drawType === 'motion') {
            const canvas = fabricCanvas.current;
            const center = selectedObject.getCenterPoint();

            // Check for existing route
            console.log("StartDrawing selected:", selectedObject?.data);
            let targetId = selectedObject.data?.id;
            if (selectedObject.data?.type === 'poly_handle') {
                targetId = selectedObject.data?.routeId;
                console.log("Handle detected, targetId:", targetId);
            }

            // Find route of the SAME subType
            const existingSameRoute = canvas.getObjects().find(o =>
                o.data?.type === 'route_polyline' &&
                o.data?.id === targetId &&
                (o.data?.subType || 'route') === drawType
            );

            // Find existing motion if drawing regular route (for extension from motion)
            const existingMotionRef = (drawType === 'route') ? canvas.getObjects().find(o =>
                o.data?.type === 'route_polyline' &&
                o.data?.id === targetId &&
                o.data?.subType === 'motion'
            ) : null;

            console.log("Existing Route Found:", !!existingSameRoute);
            console.log("Existing Motion Found (for cross-extension):", !!existingMotionRef);

            if (existingSameRoute) {
                // Resume Drawing / Extension Mode
                console.log("Resuming drawing for existing route (Extension Mode)");
                activePolyline.current = existingSameRoute;
                isExtendingRef.current = true; // Mark as extension to override limit

                // Proceed to join at the tip
                const lastPoint = existingSameRoute.points[existingSameRoute.points.length - 1];

                // Calculate tip position on canvas
                const matrix = existingSameRoute.calcTransformMatrix();
                const relPoint = {
                    x: lastPoint.x - (existingSameRoute.pathOffset.x || 0),
                    y: lastPoint.y - (existingSameRoute.pathOffset.y || 0)
                };
                const pointOnCanvas = fabric.util.transformPoint(relPoint, matrix);

                // Create Preview Line from tip
                const prev = new fabric.Line([pointOnCanvas.x, pointOnCanvas.y, pointOnCanvas.x, pointOnCanvas.y], {
                    stroke: '#facc15', // Yellow
                    strokeWidth: 2,
                    strokeDashArray: [5, 5],
                    selectable: false,
                    evented: false,
                    opacity: 0.6
                });
                canvas.add(prev);
                previewLine.current = prev;

                // Add Listeners
                canvas.on('mouse:move', onRouteMouseMove);
                canvas.on('mouse:down', onRouteMouseDown);
                canvas.on('mouse:dblclick', finishDrawing);

                activeRouteHandlers.current.down = onRouteMouseDown;
                activeRouteHandlers.current.move = onRouteMouseMove;

                // DISABLE handles during extension
                existingSameRoute.selectable = false;
                existingSameRoute.evented = false;
            } else if (existingMotionRef && drawType === 'route') {
                // SPECIAL CASE: Extension from Motion to Regular Route
                isExtendingRef.current = false; // It's a "New" solid route starting from an old end

                const lastPoint = existingMotionRef.points[existingMotionRef.points.length - 1];
                const matrix = existingMotionRef.calcTransformMatrix();
                const relPoint = {
                    x: lastPoint.x - (existingMotionRef.pathOffset.x || 0),
                    y: lastPoint.y - (existingMotionRef.pathOffset.y || 0)
                };
                const pointOnCanvas = fabric.util.transformPoint(relPoint, matrix);

                const startPoint = { x: pointOnCanvas.x, y: pointOnCanvas.y };

                // Create Regular Polyline (Solid) starting at motion end
                const poly = new fabric.Polyline([startPoint], {
                    stroke: '#facc15',
                    strokeWidth: 4,
                    fill: 'transparent',
                    strokeLineCap: 'round',
                    strokeLineJoin: 'round',
                    objectCaching: false,
                    selectable: false,
                    evented: false,
                    originX: 'left',
                    originY: 'top',
                    data: {
                        type: 'route_polyline',
                        subType: 'route',
                        id: targetId,
                        parentId: existingMotionRef.data?.id, // Optional: track parent
                        endType: routeEndType
                    }
                });

                canvas.add(poly);
                canvas.sendObjectToBack(poly);
                if (canvas.backgroundImage) canvas.sendObjectToBack(canvas.backgroundImage);
                // Send background to back again to ensure it's at the VERY bottom
                if (canvas.backgroundImage) canvas.sendObjectToBack(canvas.backgroundImage);

                activePolyline.current = poly;

                // Create Preview Line
                const prev = new fabric.Line([startPoint.x, startPoint.y, startPoint.x, startPoint.y], {
                    stroke: '#facc15',
                    strokeWidth: 2,
                    strokeDashArray: [5, 5],
                    selectable: false,
                    evented: false,
                    opacity: 0.6
                });
                canvas.add(prev);
                previewLine.current = prev;

                canvas.on('mouse:move', onRouteMouseMove);
                canvas.on('mouse:down', onRouteMouseDown);
                canvas.on('mouse:dblclick', finishDrawing);

                activeRouteHandlers.current = { move: onRouteMouseMove, down: onRouteMouseDown };

            } else {
                // New Drawing Mode
                isExtendingRef.current = false; // Standard mode (limit applies)
                const startPoint = { x: center.x, y: center.y };

                // Determine End Type: Auto-Block for OL
                // Check data.position, data.label, or inner text
                const getLabel = (o) => o.data?.position || o.data?.label || (o.getObjects ? o.getObjects().find(x => x.text)?.text : '');
                const label = getLabel(selectedObject);
                const isOL = ['OL', 'C', 'G', 'T', 'LT', 'LG', 'RG', 'RT'].includes(label);

                const initialEndType = isOL ? 'block' : routeEndType;

                // Sync UI State if needed
                if (isOL && routeEndType !== 'block') {
                    setRouteEndType('block');
                }
                // New Drawing
                console.log("Starting new drawing for player:", targetId);
                // Initialize with (0,0) as relative point
                const poly = new fabric.Polyline([{ x: 0, y: 0 }], {
                    stroke: (drawType === 'motion') ? '#60a5fa' : '#facc15',
                    strokeWidth: 4,
                    fill: 'transparent',
                    strokeLineCap: 'round',
                    strokeLineJoin: 'round',
                    left: startPoint.x,
                    top: startPoint.y,
                    objectCaching: false,
                    selectable: false,
                    evented: false,
                    originX: 'left',
                    originY: 'top',
                    data: {
                        type: 'route_polyline',
                        subType: drawType,
                        id: String(targetId),
                        endType: (drawType === 'motion') ? 'dashed' : 'arrow'
                    },
                    strokeDashArray: (drawType === 'motion') ? [6, 4] : null
                });

                canvas.add(poly);
                canvas.sendObjectToBack(poly);
                if (canvas.backgroundImage) canvas.sendObjectToBack(canvas.backgroundImage);
                // Send background to back again to ensure it's at the VERY bottom
                if (canvas.backgroundImage) canvas.sendObjectToBack(canvas.backgroundImage);

                activePolyline.current = poly;

                // Create Preview Line
                const prev = new fabric.Line([startPoint.x, startPoint.y, startPoint.x, startPoint.y], {
                    stroke: '#facc15', // Yellow
                    strokeWidth: 2,
                    strokeDashArray: [4, 4],
                    selectable: false,
                    evented: false,
                    opacity: 0.6
                });
                canvas.add(prev);
                previewLine.current = prev;

                // Add Listeners
                canvas.on('mouse:move', onRouteMouseMove);
                canvas.on('mouse:down', onRouteMouseDown);
                canvas.on('mouse:dblclick', finishDrawing); // Finish on double click

                activeRouteHandlers.current = { move: onRouteMouseMove, down: onRouteMouseDown };




            }
        }
    };


    // --- POLYLINE EVENTS ---

    const onRouteMouseMove = useCallback((opt) => {
        if (!previewLine.current || !activePolyline.current) return;
        const canvas = fabricCanvas.current;
        const ptr = canvas.getPointer(opt.e);

        // Update Preview Line
        previewLine.current.set({ x2: ptr.x, y2: ptr.y });
        canvas.requestRenderAll();
    }, []);

    const onRouteMouseDown = useCallback((opt) => {
        const canvas = fabricCanvas.current;
        if (!canvas) return;

        const ptr = canvas.getPointer(opt.e);

        // Spatial Exclusion: Check if we clicked on an existing object (Handle)
        if (opt.target && (opt.target.data?.type === 'poly_handle' || opt.target.data?.type === 'route_handle')) {
            console.log("Spatial Exclusion: Clicked Handle (Target Check) - Aborting Point Add");
            return;
        }

        if (!activePolyline.current || !previewLine.current) return;

        const poly = activePolyline.current;

        // --- LIMIT CHECKING ---
        // Find owner to determine if OL
        const routeId = poly.data?.id;
        const owner = canvas.getObjects().find(o => o.data?.id === routeId && (o.data?.type === 'O' || o.data?.type === 'D'));
        const getLabel = (o) => o?.data?.position || o?.data?.label || (o?.getObjects ? o.getObjects().find(x => x.text)?.text : '');
        const label = getLabel(owner);
        const isOL = ['OL', 'C', 'G', 'T', 'LT', 'LG', 'RG', 'RT'].includes(label);

        // Determine Limit (Points Count)
        // OL Initial: 2 points (Start + 1 End) -> 1 Segment
        // Standard Initial: 3 points (2 Segments)
        // Defense (Blitz): 2 points (1 Segment)
        const isZone = poly.data?.subType === 'zone';
        const isDefense = playbookType === 'defense';
        // Limit: Zone=99, OL/Defense=2, Offense=3
        const limit = isZone ? 99 : ((isOL || isDefense) ? 2 : 3);

        // Pre-Check: If strictly enforcing limit before adding (prevent extra clicks)
        if (!isExtendingRef.current && poly.points.length >= limit) {
            finishDrawing();
            return;
        }

        let target = opt.target;
        // Fallback: If primary target is blocked (e.g. by another line/handle), search for zone at this point
        if (!target || target.data?.type !== 'zone_coverage') {
            const zones = canvas.getObjects().filter(obj => obj.data?.type === 'zone_coverage');
            target = zones.find(z => z.containsPoint(ptr)) || target;
        }

        let forceFinish = false;

        // ZONE SNAP LOGIC
        if (target && target.data?.type === 'zone_coverage') {
            const zone = target;
            console.log("Zone Snap: Force Snapping to Center of Zone", zone.data.id);
            zone.setCoords();
            const center = zone.getCenterPoint();

            // USE updatePolylineShape for robust snap
            const newIndex = poly.points.length;
            poly.points.push(new fabric.Point(0, 0)); // Placeholder
            updatePolylineShape(poly, newIndex, center);

            forceFinish = true;
            poly.data.endType = 'none'; // No arrowhead for zone connection
            poly.data.connectedZoneId = String(zone.data.id); // Store link to zone
        } else {
            // Normal point addition
            const newIndex = poly.points.length;
            poly.points.push(new fabric.Point(0, 0)); // Placeholder
            updatePolylineShape(poly, newIndex, { x: ptr.x, y: ptr.y });
        }

        // Check Finish Condition Immediately
        // Extension: 1 segment always finishes.
        // Initial: Limit check (OL=2, Std=3).
        // Zone Snap: Always finishes.
        const willFinish = isExtendingRef.current || (poly.points.length >= limit) || forceFinish;

        if (willFinish) {
            console.log("Limit Reached - Auto Finishing Immediately");
            // Points updated via updatePolylineShape
            finishDrawing();
            return;
        }

        // Continue Drawing
        // Update Preview Line Start to the absolute position of the last point added
        const lastPt = poly.points[poly.points.length - 1];
        const matrix = poly.calcTransformMatrix();
        const absLastPt = fabric.util.transformPoint({
            x: lastPt.x - (poly.pathOffset.x || 0),
            y: lastPt.y - (poly.pathOffset.y || 0)
        }, matrix);

        previewLine.current.set({ x1: absLastPt.x, y1: absLastPt.y, x2: absLastPt.x, y2: absLastPt.y });

        canvas.renderAll();
        saveToHistory();
    }, [playbookType, finishDrawing, saveToHistory]);

    // Handlers defined inside component to access state/refs
    // Simplified logic: Click to set endpoint. Click again to extend?
    // User requested: "Click empty space to finish", "Green check button to finish"

    const onMouseMove = useCallback((opt) => {
        if (!tempLine.current) return;
        const ptr = fabricCanvas.current.getPointer(opt.e);
        tempLine.current.set({ x2: ptr.x, y2: ptr.y });
        fabricCanvas.current.renderAll();
    }, []);

    const onMouseDown = useCallback((opt) => {
        // Logic: 
        // 1. If clicked on empty space -> Set segment, continue drawing from new point?
        // User said: "Current route drawing creates new line on every click... green check to finish"
        // Let's implement polyline style behavior

        const ptr = fabricCanvas.current.getPointer(opt.e);

        // Spatial Exclusion: Check if we clicked on an existing object (Handle)
        if (opt.target && (opt.target.data?.type === 'poly_handle' || opt.target.data?.type === 'route_handle')) {
            console.log("Legacy Draw: Spatial Exclusion - Clicked Handle (Target Check) - Aborting Point Add");
            return;
        }

        // FIX: If we just clicked, we solidified the temp line.
        // Convert temp line to permanent line
        const newLine = new fabric.Line([tempLine.current.x1, tempLine.current.y1, ptr.x, ptr.y], {
            stroke: tempLine.current.stroke,
            strokeWidth: tempLine.current.strokeWidth,
            strokeDashArray: tempLine.current.strokeDashArray,
            selectable: false,
            evented: false
        });

        fabricCanvas.current.add(newLine);
        lastDrawnLine.current = newLine;

        // Move temp line start to new point
        tempLine.current.set({ x1: ptr.x, y1: ptr.y, x2: ptr.x, y2: ptr.y });

        saveToHistory();
    }, []);

    // finishDrawing moved to line ~1877 to resolve circular dependency



    // --- RENDER ---
    return (
        <TooltipProvider>
            <div className="flex flex-col h-full w-full bg-[#0a0a0b] overflow-hidden">
                {/* Header - Mobile Optimized */}
                <div className="h-16 border-b border-gray-800 bg-[#161617] flex items-center justify-between px-4 z-10 shrink-0 safe-top">
                    <div className="flex items-center gap-2">
                        <h2 className="font-bold text-white tracking-tight truncate max-w-[150px]">
                            {playName || t('playDesigner.newPlay')} <span className="text-gray-500 text-xs font-mono">(v2.2)</span>
                        </h2>
                    </div>

                    <div className="flex gap-3">
                        {isPlaying ? (
                            <Button variant="outline" size="icon" onClick={resetPlay} className="border-gray-700 bg-black/20 text-white hover:bg-black/40 rounded-xl">
                                <RefreshCw className="w-5 h-5" />
                            </Button>
                        ) : (
                            <Button variant="outline" size="icon" onClick={runPlay} className="border-gray-700 bg-black/20 text-white hover:bg-black/40 rounded-xl">
                                <Play className="w-5 h-5 fill-current" />
                            </Button>
                        )}
                        <Button variant="ghost" onClick={onCancel} className="text-gray-400 font-medium whitespace-nowrap">
                            {t('playDesigner.cancel')}
                        </Button>
                        <Button onClick={() => {
                            const canvasJson = fabricCanvas.current.toJSON(['data', 'pathOffset']);
                            // Include canvas dimensions for proportional loading
                            canvasJson.savedWidth = fabricCanvas.current.width;
                            canvasJson.savedHeight = fabricCanvas.current.height;
                            onSave(JSON.stringify(canvasJson));
                        }} className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 whitespace-nowrap">
                            {t('playDesigner.save')}
                        </Button>
                    </div>
                </div>

                {/* Main Canvas Area */}
                <div ref={containerRef} className="flex-1 relative bg-[#2D2D2D] touch-none">
                    <canvas ref={canvasRef} className="w-full h-full" />

                    {/* Floating Toolbar (Left) */}
                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                        <Button variant="secondary" size="icon" onClick={undo} className="rounded-full shadow-lg bg-black/50 text-white border-none hover:bg-black/70" title="Undo">
                            <Undo2 className="w-5 h-5" />
                        </Button>
                        <Button variant="secondary" size="icon" onClick={() => setIsMoveMode(!isMoveMode)} className={cn("rounded-full shadow-lg border-none hover:bg-black/70", isMoveMode ? "bg-blue-600 text-white" : "bg-black/50 text-white")} title="Move Players">
                            <Move className="w-5 h-5" />
                        </Button>

                        {/* Add Offensive Player Button (Only for Defense - Scout Team) */}
                        {playbookType === 'defense' && (
                            <Button variant="secondary" size="icon" onClick={addOffensivePlayer} className="rounded-full shadow-lg bg-black/50 text-white border-none hover:bg-black/70" title="Add Offensive Player">
                                <Plus className="w-5 h-5" />
                            </Button>
                        )}


                        <Button variant="secondary" size="icon" onClick={() => {
                            // Flip Logic - Mirror ONLY players and routes horizontally
                            const canvas = fabricCanvas.current;
                            if (!canvas) return;

                            const centerX = canvas.width / 2;

                            canvas.getObjects().forEach(obj => {
                                // ONLY flip players (O/D), routes, route ends, and man coverage links
                                const type = obj.data?.type;
                                const isPlayer = type === 'O' || type === 'D';
                                const isRoute = type === 'route_polyline' || type === 'route_end' || type === 'man_coverage_link';

                                if (!isPlayer && !isRoute) return;

                                // Flip position around center
                                const objCenterX = obj.left;
                                const newX = centerX + (centerX - objCenterX);
                                obj.set({ left: newX });

                                // For polylines, flip each point
                                if (type === 'route_polyline' && obj.points) {
                                    obj.points.forEach(p => {
                                        const oldX = p.x;
                                        p.x = obj.pathOffset.x * 2 - oldX;
                                    });
                                    obj._calcDimensions();
                                    obj.set({ dirty: true });
                                }

                                obj.setCoords();
                            });

                            canvas.requestRenderAll();
                            saveToHistory();
                        }} className="rounded-full shadow-lg bg-black/50 text-white border-none hover:bg-black/70" title="Flip Play">
                            <MoveHorizontal className="w-5 h-5" />
                        </Button>
                        <Button variant="destructive" size="icon" onClick={() => {
                            const active = fabricCanvas.current?.getActiveObjects();
                            if (active?.length) {
                                fabricCanvas.current.remove(...active);
                                fabricCanvas.current.discardActiveObject();
                                fabricCanvas.current.renderAll();
                            }
                        }} className="rounded-full shadow-lg bg-red-500/80 text-white border-none hover:bg-red-600" title="Delete Selected">
                            <Trash2 className="w-5 h-5" />
                        </Button>
                    </div>


                    {/* Floating Formations Button (Right Top) */}
                    <div className="absolute top-4 right-4 flex flex-col gap-2">
                        {/* Show for Offense primarily, or maybe both? Usually Offense Formations. */}
                        {/* Existing button was: */}
                        <Button
                            onClick={() => setShowFormationPicker(true)}
                            className="rounded-full shadow-lg bg-blue-600 text-white font-bold"
                        >
                            {t('playDesigner.formations')}
                        </Button>
                    </div>


                    {/* Contextual Route Tools (Bottom Center) - Only when object selected */}
                    {/* Contextual Route Tools (Bottom Center) - Only when player selected */}
                    {selectedObject && (selectedObject.data?.type === 'O' || selectedObject.data?.type === 'D') && (
                        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-[#161617]/90 backdrop-blur-md border border-gray-700 p-2 rounded-2xl flex gap-2 shadow-2xl animate-in slide-in-from-bottom-5">
                            {/* Left Button: Start Drawing Route */}
                            <Button variant="ghost" size="icon" onClick={() => {
                                // Turn off other modes
                                setIsEditMode(false);
                                setIsCurveMode(false);
                                isEditModeRef.current = false;
                                isCurveModeRef.current = false;
                                startDrawing('route');
                            }} className={cn("text-yellow-400 hover:bg-white/10", drawingMode === 'route' && "bg-white/20")}>
                                {fabricCanvas.current?.getObjects().some(o => o.data?.type === 'route_polyline' && o.data?.id === selectedObject.data?.id) ? (
                                    <Plus className="w-6 h-6" />
                                ) : (
                                    <ArrowUpRight className="w-6 h-6" />
                                )}
                            </Button>

                            {/* Middle Tool: Conditional based on Playbook Type */}
                            {playbookType === 'defense' ? (
                                /* Zone Coverage Tool (Defense Only) */
                                <div className="relative">
                                    {/* Zone Menu Popover */}
                                    {showZoneMenu && (
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-[#161617] border border-gray-700 rounded-xl p-1 flex flex-col gap-1 shadow-xl animate-in fade-in slide-in-from-bottom-2 z-50">
                                            <Button variant="ghost" size="icon" onClick={() => addZoneShape('rect')} className="text-gray-400 hover:text-white hover:bg-white/10" title={t('playDesigner.rectangle')}>
                                                <Square className="w-5 h-5 fill-current opacity-50" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => addZoneShape('ellipse')} className="text-gray-400 hover:text-white hover:bg-white/10" title={t('playDesigner.ellipse')}>
                                                <Circle className="w-5 h-5 fill-current opacity-50" />
                                            </Button>
                                        </div>
                                    )}
                                    <Button variant="ghost" size="icon" onClick={() => {
                                        setShowZoneMenu(!showZoneMenu);
                                        // Turn off other modes
                                        setIsEditMode(false);
                                        setIsCurveMode(false);
                                        isEditModeRef.current = false;
                                        isCurveModeRef.current = false;
                                        setDrawingMode(null);
                                    }} className={cn("text-gray-400 hover:bg-white/10", showZoneMenu && "bg-white/20 text-yellow-400")}>
                                        <Shield className="w-6 h-6" />
                                    </Button>
                                </div>
                            ) : (
                                /* Motion Tool (Offense Only) */
                                <Button variant="ghost" size="icon" onClick={() => {
                                    // Turn off other modes
                                    setIsEditMode(false);
                                    setIsCurveMode(false);
                                    isEditModeRef.current = false;
                                    isCurveModeRef.current = false;
                                    startDrawing('motion');
                                }} className={cn("text-gray-400 hover:bg-white/10", drawingMode === 'motion' && "bg-white/20 text-blue-400")}>
                                    <MoveHorizontal className="w-6 h-6" />
                                </Button>
                            )}

                            {/* Edit Mode Toggle */}
                            <Button variant="ghost" size="icon" onClick={() => {
                                const newEdit = !isEditMode;
                                if (newEdit) {
                                    // Mutual Exclusivity: Turn off drawing and curve
                                    if (drawingMode) finishDrawing();
                                    setIsCurveMode(false);
                                    isCurveModeRef.current = false;
                                }
                                setIsEditMode(newEdit);
                            }} className={cn("hover:bg-white/10", isEditMode ? "text-green-400 bg-white/20" : "text-gray-400")}>
                                <Pencil className="w-5 h-5" />
                            </Button>

                            {/* Conditional: Curve Mode (Offense) / Man Coverage (Defense) */}
                            {playbookType === 'defense' ? (
                                /* Man Coverage Toggle (Defense Only) */
                                <Button variant="ghost" size="icon" onClick={() => {
                                    // Toggle Man Coverage Mode
                                    if (drawingMode === 'man_coverage') {
                                        setDrawingMode(null);
                                        if (fabricCanvas.current) {
                                            fabricCanvas.current.defaultCursor = 'default';
                                            fabricCanvas.current.off('mouse:down', onManCoverageMouseDown);
                                        }
                                    } else {
                                        // Enter Mode
                                        setDrawingMode('man_coverage');
                                        setIsEditMode(false);
                                        setIsCurveMode(false);
                                        if (fabricCanvas.current) {
                                            fabricCanvas.current.defaultCursor = 'crosshair';
                                            fabricCanvas.current.on('mouse:down', onManCoverageMouseDown);
                                        }
                                    }
                                }} className={cn("hover:bg-white/10", drawingMode === 'man_coverage' ? "text-red-400 bg-white/20" : "text-gray-400")} title="Man Coverage (Link to Offense)">
                                    <Link className="w-5 h-5" />
                                </Button>
                            ) : (
                                /* Curve Mode Toggle (Offense Only) */
                                <Button variant="ghost" size="icon" onClick={() => {
                                    const newCurve = !isCurveMode;
                                    if (newCurve) {
                                        // Mutual Exclusivity: Turn off drawing and edit
                                        if (drawingMode) finishDrawing();
                                        setIsEditMode(false);
                                        isEditModeRef.current = false;
                                    }
                                    setIsCurveMode(newCurve);
                                    isCurveModeRef.current = newCurve;
                                }} className={cn("hover:bg-white/10", isCurveMode ? "text-blue-400 bg-white/20" : "text-gray-400")} title="Curve Mode">
                                    <Spline className="w-5 h-5" />
                                </Button>
                            )}

                            {/* Right Button: Toggle Route Style (Arrow / Stop / Block) */}
                            <Button variant="ghost" size="icon" onClick={() => {
                                // Cycle: Arrow -> Stop -> Block -> Arrow
                                let nextType = 'arrow';
                                if (routeEndType === 'arrow') nextType = 'stop';
                                else if (routeEndType === 'stop') nextType = 'block';

                                setRouteEndType(nextType);

                                // Update current route immediately if selected
                                let route = activePolyline.current;
                                if (!route && selectedObject) {
                                    const targetId = selectedObject.data?.id;
                                    route = fabricCanvas.current?.getObjects().find(o => o.data?.type === 'route_polyline' && o.data?.id === targetId);
                                }

                                if (route) {
                                    activePolyline.current = route;
                                    updateRouteArrow(route, nextType);
                                }
                            }} className={cn("hover:bg-white/10", routeEndType !== 'arrow' ? "text-yellow-400" : "text-gray-400")}>
                                {routeEndType === 'stop' ? (
                                    <Circle className="w-5 h-5 fill-current" />
                                ) : routeEndType === 'block' ? (
                                    <Minus className="w-5 h-5 rotate-90" />
                                ) : (
                                    <ArrowUpRight className="w-5 h-5" />
                                )}
                            </Button>

                            {drawingMode && (
                                <div className="w-px h-8 bg-gray-700 mx-1" />
                            )}

                            {drawingMode && (
                                <Button variant="default" size="icon" onClick={finishDrawing} className="bg-green-600 hover:bg-green-700 text-white rounded-xl">
                                    <Check className="w-5 h-5" />
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {/* Formation Picker Modal (Overlay) */}
                {showFormationPicker && (
                    <div className="absolute inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-[#161617] w-full max-w-lg rounded-2xl border border-gray-800 flex flex-col max-h-[80vh] overflow-hidden">
                            <div className="p-4 border-b border-gray-800 flex justify-between items-center">
                                <h3 className="text-white font-bold text-lg">
                                    {playbookType === 'defense' ? 'Savunma Formasyonu Seç' : t('playDesigner.selectFormation')}
                                </h3>
                                <Button variant="ghost" size="icon" onClick={() => setShowFormationPicker(false)}>
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-2">
                                {/* Formations List */}
                                <div className="space-y-2 mb-6">
                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-1">
                                        {t('playDesigner.formations')}
                                    </h4>
                                    {Object.keys(playbookType === 'defense' ? DEFENSE_FORMATIONS : FORMATION_OFFSETS).map(key => (
                                        <button
                                            key={key}
                                            onClick={() => {
                                                addFormation(key);
                                                setShowFormationPicker(false);
                                            }}
                                            className="w-full text-left p-4 rounded-xl bg-gray-800/50 hover:bg-blue-600/20 border border-gray-700 hover:border-blue-500 transition-all flex items-center justify-between group"
                                        >
                                            <span className="font-bold text-gray-200 group-hover:text-blue-400">
                                                {key.replace(/([A-Z])/g, ' $1').toUpperCase()}
                                            </span>
                                            <Layout className="w-5 h-5 text-gray-500 group-hover:text-blue-400" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </TooltipProvider >
    );
};

export default PlayDesigner;
